import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { axios } from '../../context/AuthContext';
import toast from 'react-hot-toast';

// ══════════════════════════════════════════════════════════════════════════════
// SABITLER
// ══════════════════════════════════════════════════════════════════════════════
const TUM_GUNLER = ['Pazartesi', 'Sali', 'Carsamba', 'Persembe', 'Cuma', 'Cumartesi', 'Pazar'];
const GUN_ETIKETLERI = {
  Pazartesi: 'Pazartesi', Sali: 'Salı', Carsamba: 'Çarşamba',
  Persembe: 'Perşembe', Cuma: 'Cuma', Cumartesi: 'Cumartesi', Pazar: 'Pazar'
};
const GUN_KISA = {
  Pazartesi: 'Pzt', Sali: 'Sal', Carsamba: 'Çar',
  Persembe: 'Per', Cuma: 'Cum', Cumartesi: 'Cmt', Pazar: 'Paz'
};

const VARSAYILAN_TENEFUSLER = [
  { tip: 'tenefus', bas: '09:40', bit: '09:50', ad: 'Tenefüs 1' },
  { tip: 'tenefus', bas: '10:30', bit: '10:40', ad: 'Tenefüs 2' },
  { tip: 'tenefus', bas: '11:20', bit: '11:30', ad: 'Tenefüs 3' },
  { tip: 'ogle', bas: '12:10', bit: '13:00', ad: 'Öğle Arası' },
  { tip: 'tenefus', bas: '13:40', bit: '13:50', ad: 'Tenefüs 5' },
  { tip: 'tenefus', bas: '14:30', bit: '14:40', ad: 'Tenefüs 6' },
  { tip: 'tenefus', bas: '15:20', bit: '15:30', ad: 'Tenefüs 7' },
];

const VARSAYILAN_GUN_SAATLERI = {
  Pazartesi: { bas: '09:00', bit: '17:00', aktif: true },
  Sali: { bas: '09:00', bit: '17:00', aktif: true },
  Carsamba: { bas: '09:00', bit: '17:00', aktif: true },
  Persembe: { bas: '09:00', bit: '17:00', aktif: true },
  Cuma: { bas: '09:00', bit: '17:00', aktif: true },
  Cumartesi: { bas: '09:00', bit: '13:00', aktif: false },
  Pazar: { bas: '09:00', bit: '13:00', aktif: false },
};

const RENK_PALETI = [
  '#EF4444', '#F97316', '#F59E0B', '#10B981', '#06B6D4',
  '#3B82F6', '#8B5CF6', '#EC4899', '#14B8A6', '#84CC16',
];

// ══════════════════════════════════════════════════════════════════════════════
// YARDIMCI FONKSİYONLAR
// ══════════════════════════════════════════════════════════════════════════════
function saatStr(h, m) {
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function dakikaYap(saatStr) {
  const [h, m] = saatStr.split(':').map(Number);
  return h * 60 + m;
}

/**
 * Bir gün için tenefüsleri atlayarak ders saatleri üretir.
 * Her ders sureDk dakika sürer.
 */
function gunIcinSaatlerUret(basDk, bitDk, sureDk, tenefusler) {
  const saatler = [];
  let current = basDk;

  while (current + sureDk <= bitDk) {
    const slotBit = current + sureDk;

    // Bu slot herhangi bir tenefüs ile çakışıyor mu?
    const cakisan = tenefusler.find(t => {
      const tBas = dakikaYap(t.bas);
      const tBit = dakikaYap(t.bit);
      return current < tBit && slotBit > tBas;
    });

    if (!cakisan) {
      saatler.push({ bas: saatStr(Math.floor(current / 60), current % 60), bit: saatStr(Math.floor(slotBit / 60), slotBit % 60) });
      current = slotBit;
    } else {
      // Tenefüsü atla, sonrasından devam et
      const tBit = dakikaYap(cakisan.bit);
      current = Math.max(current + 1, tBit);
    }
  }
  return saatler;
}

/**
 * Tüm günler için saatler haritası üretir.
 * { 'Pazartesi': [{bas, bit}, ...], ... }
 */
function tumGunlerIcinSaatler(gunSaatleri, sureDk, tenefusler) {
  const result = {};
  TUM_GUNLER.forEach((gun) => {
    const ayar = gunSaatleri[gun];
    if (!ayar) return;
    if (!ayar.aktif) return;
    const basDk = dakikaYap(ayar.bas);
    const bitDk = dakikaYap(ayar.bit);
    result[gun] = gunIcinSaatlerUret(basDk, bitDk, sureDk, tenefusler);
  });
  return result;
}

function getAktifGunler(gunSaatleri) {
  return TUM_GUNLER.filter(gun => gunSaatleri?.[gun]?.aktif);
}

function normalizeGunSaatleri(gunSaatleri = {}) {
  const normalized = {};
  TUM_GUNLER.forEach((gun) => {
    const mevcut = gunSaatleri?.[gun] || {};
    const varsayilan = VARSAYILAN_GUN_SAATLERI[gun] || { bas: '09:00', bit: '17:00', aktif: false };
    normalized[gun] = {
      bas: mevcut.bas || varsayilan.bas,
      bit: mevcut.bit || varsayilan.bit,
      aktif: mevcut.aktif ?? varsayilan.aktif,
    };
  });
  return normalized;
}

function temizMetinDegeri(deger = '') {
  return deger
    .toString()
    .toLocaleLowerCase('tr-TR')
    .replace(/\./g, '')
    .trim();
}

function normalizeSeviye(seviye = '') {
  return seviye.toString().replace('. Sınıf', '').trim();
}

const TOPLU_MUFREDAT_HEDEFLERI = [
  { id: '5', label: 'Tüm 5. Sınıflar', seviyeler: ['5'] },
  { id: '6', label: 'Tüm 6. Sınıflar', seviyeler: ['6'] },
  { id: '7', label: 'Tüm 7. Sınıflar', seviyeler: ['7'] },
  { id: '8', label: 'Tüm 8. Sınıflar', seviyeler: ['8'] },
  { id: '9-10', label: '9-10. Sınıflar', seviyeler: ['9', '10'] },
  { id: '11-12-mezun', label: '11-12-Mezun', seviyeler: ['11', '12', 'Mezun'], turSecimi: true },
];

function sinifHedefeUyuyorMu(sinif, hedefId, tur = '') {
  const hedef = TOPLU_MUFREDAT_HEDEFLERI.find(item => item.id === hedefId);
  if (!hedef) return false;

  const sinifSeviye = normalizeSeviye(sinif.seviye);
  const seviyeUyumlu = hedef.seviyeler.includes(sinifSeviye);
  if (!seviyeUyumlu) return false;

  if (!tur) return true;
  return temizMetinDegeri(sinif.tur) === temizMetinDegeri(tur);
}

// ══════════════════════════════════════════════════════════════════════════════
// ADIM 1: GENEL AYARLAR - Her gün için ayrı başlangıç/bitiş
// ══════════════════════════════════════════════════════════════════════════════
function Adim1GenelAyarlar({ ayarlar, setAyarlar, isMobile = false }) {
  const { gunSaatleri, dersSuresiDk, tenefusler } = ayarlar;

  // Tüm günler için saatleri hesapla (anlık önizleme)
  const gunSlotlari = tumGunlerIcinSaatler(gunSaatleri, dersSuresiDk, tenefusler);
  const aktifGunler = getAktifGunler(gunSaatleri);

  const gunToggle = (gun) => {
    setAyarlar(prev => ({
      ...prev,
      gunSaatleri: {
        ...prev.gunSaatleri,
        [gun]: { ...prev.gunSaatleri[gun], aktif: !prev.gunSaatleri[gun].aktif }
      }
    }));
  };

  const gunSaatGuncelle = (gun, field, val) => {
    setAyarlar(prev => ({
      ...prev,
      gunSaatleri: {
        ...prev.gunSaatleri,
        [gun]: { ...prev.gunSaatleri[gun], [field]: val }
      }
    }));
  };

  const tenefusEkle = () => {
    setAyarlar(prev => ({
      ...prev,
      tenefusler: [...prev.tenefusler, { tip: 'tenefus', bas: '10:00', bit: '10:10', ad: 'Yeni Tenefüs' }]
    }));
  };

  const tenefusSil = (i) => {
    setAyarlar(prev => ({ ...prev, tenefusler: prev.tenefusler.filter((_, idx) => idx !== i) }));
  };

  const tenefusGuncelle = (i, field, val) => {
    setAyarlar(prev => {
      const yeni = [...prev.tenefusler];
      yeni[i] = { ...yeni[i], [field]: val };
      return { ...prev, tenefusler: yeni };
    });
  };

  // Tüm hafta toplamı
  const toplamHaftaSlot = aktifGunler.reduce((acc, gun) => acc + (gunSlotlari[gun]?.length || 0), 0);
  const toplamDakika = aktifGunler.reduce((acc, gun) => acc + (gunSlotlari[gun]?.length || 0) * dersSuresiDk, 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 14 : 20, width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
      {/* Özet Kart */}
      {aktifGunler.length > 0 && (
        <div style={{
          background: 'linear-gradient(135deg, var(--accent) 0%, #8B5CF6 100%)',
          borderRadius: 14, padding: isMobile ? '12px 14px' : '16px 20px',
          display: 'flex', gap: isMobile ? 18 : 32, flexWrap: 'wrap', alignItems: 'center'
        }}>
          <div style={{ color: '#fff' }}>
            <div style={{ fontSize: '0.75rem', opacity: 0.8 }}>Aktif Gün</div>
            <div style={{ fontFamily: 'var(--font-ui)', fontSize: isMobile ? '1.35rem' : '1.8rem', fontWeight: 900, lineHeight: 1 }}>{aktifGunler.length}</div>
          </div>
          <div style={{ color: '#fff' }}>
            <div style={{ fontSize: '0.75rem', opacity: 0.8 }}>Haftalık Toplam Slot</div>
            <div style={{ fontFamily: 'var(--font-ui)', fontSize: isMobile ? '1.35rem' : '1.8rem', fontWeight: 900, lineHeight: 1 }}>{toplamHaftaSlot}</div>
          </div>
          <div style={{ color: '#fff' }}>
            <div style={{ fontSize: '0.75rem', opacity: 0.8 }}>Ders Süresi</div>
            <div style={{ fontFamily: 'var(--font-ui)', fontSize: isMobile ? '1.35rem' : '1.8rem', fontWeight: 900, lineHeight: 1 }}>{dersSuresiDk} dk</div>
          </div>
          <div style={{ color: '#fff' }}>
            <div style={{ fontSize: '0.75rem', opacity: 0.8 }}>Haftalık Ders Saati</div>
            <div style={{ fontFamily: 'var(--font-ui)', fontSize: isMobile ? '1.35rem' : '1.8rem', fontWeight: 900, lineHeight: 1 }}>{Math.floor(toplamDakika / 60)}s {toplamDakika % 60}dk</div>
          </div>
        </div>
      )}

      {/* Ders Süresi */}
      <div className="card" style={{ padding: isMobile ? 14 : 20 }}>
        <h4 style={{ marginBottom: 16, color: '#3B82F6', fontFamily: 'var(--font-ui)' }}>⏱ Ders Süresi</h4>
        <div style={{ display: 'flex', alignItems: isMobile ? 'stretch' : 'center', gap: 16, flexDirection: isMobile ? 'column' : 'row' }}>
          <div className="form-group" style={{ margin: 0, width: isMobile ? '100%' : 200 }}>
            <label className="form-label">Ders Süresi (Dakika)</label>
            <input type="number" className="form-input" min={20} max={90} step={5}
              value={dersSuresiDk}
              onChange={e => setAyarlar(prev => ({ ...prev, dersSuresiDk: parseInt(e.target.value) || 40 }))} />
          </div>
          <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', paddingTop: isMobile ? 0 : 20 }}>
            Tenefüsler bu süreye göre otomatik hesaplanır.
          </div>
        </div>
      </div>

      {/* Günler & Saatler */}
      <div className="card" style={{ padding: isMobile ? 14 : 20 }}>
        <h4 style={{ marginBottom: 4, color: 'var(--accent)', fontFamily: 'var(--font-ui)' }}>📅 Aktif Günler & Ders Saatleri</h4>
        <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: 16 }}>
          Her gün için ayrı başlangıç ve bitiş saati belirleyebilirsiniz. Kapalı günlerde ders planlanmaz.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {TUM_GUNLER.map(gun => {
            const ayar = gunSaatleri[gun] || { bas: '09:00', bit: '17:00', aktif: false };
            const slotlar = gunSlotlari[gun] || [];
            const isWeekend = gun === 'Cumartesi' || gun === 'Pazar';
            return (
              <div key={gun} style={{
                borderRadius: 12,
                // 2. ÖNLEM: ayar?.aktif kullanarak undefined hatasını tamamen engelliyoruz
                border: `2px solid ${ayar?.aktif ? 'var(--accent)' : 'var(--border)'}`,
                background: ayar?.aktif ? 'var(--accent-dim)' : 'var(--bg-elevated)',
                transition: 'all 0.2s', overflow: 'hidden'
              }}>
                <div style={{ display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', gap: 12, padding: isMobile ? '10px 12px' : '10px 16px', flexDirection: isMobile ? 'column' : 'row' }}>
                  {/* Toggle Butonu */}
                  <button
                    onClick={() => gunToggle(gun)}
                    style={{
                      width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer',
                      background: ayar?.aktif ? 'var(--accent)' : 'var(--border)',
                      position: 'relative', transition: 'background 0.2s', flexShrink: 0
                    }}
                  >
                    <div style={{
                      width: 18, height: 18, borderRadius: '50%', background: '#fff',
                      position: 'absolute', top: 3,
                      left: ayar?.aktif ? 23 : 3,
                      transition: 'left 0.2s'
                    }} />
                  </button>

                  {/* Gün Adı */}
                  <span style={{
                    fontWeight: 800, fontFamily: 'var(--font-ui)', width: 100, flexShrink: 0,
                    color: ayar.aktif ? 'var(--accent)' : 'var(--text-muted)',
                    fontSize: '0.88rem'
                  }}>
                    {GUN_ETIKETLERI[gun]}
                    {isWeekend && <span style={{ fontSize: '0.65rem', marginLeft: 4, opacity: 0.7 }}>(Hft)</span>}
                  </span>

                  {/* Saatler */}
                  {ayar.aktif ? (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: isMobile ? '100%' : 'auto' }}>
                        <input type="time" className="form-input" value={ayar.bas}
                          onChange={e => gunSaatGuncelle(gun, 'bas', e.target.value)}
                          style={{ width: isMobile ? '100%' : 120, fontSize: '0.85rem', padding: '6px 10px' }} />
                        <span style={{ color: 'var(--text-muted)', fontWeight: 700 }}>—</span>
                        <input type="time" className="form-input" value={ayar.bit}
                          onChange={e => gunSaatGuncelle(gun, 'bit', e.target.value)}
                          style={{ width: isMobile ? '100%' : 120, fontSize: '0.85rem', padding: '6px 10px' }} />
                      </div>

                      {/* Slot sayısı */}
                      <div style={{ marginLeft: isMobile ? 0 : 'auto', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', width: isMobile ? '100%' : 'auto' }}>
                        {slotlar.map((s, i) => (
                          <span key={i} style={{
                            fontSize: '0.7rem', padding: '3px 8px', borderRadius: 6,
                            background: 'var(--accent)', color: '#fff', fontFamily: 'monospace', fontWeight: 700
                          }}>{s.bas}</span>
                        ))}
                        {slotlar.length > 0 && (
                          <span style={{ fontSize: '0.75rem', color: 'var(--accent)', fontWeight: 800 }}>
                            ({slotlar.length} ders)
                          </span>
                        )}
                      </div>
                    </>
                  ) : (
                    <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>Kapalı – ders planlanmaz</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Tenefüsler */}
      <div className="card" style={{ padding: isMobile ? 14 : 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', marginBottom: 16, gap: 10, flexDirection: isMobile ? 'column' : 'row' }}>
          <div>
            <h4 style={{ color: '#10B981', fontFamily: 'var(--font-ui)', margin: 0 }}>☕ Tenefüs & Öğle Araları</h4>
            <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 4, marginBottom: 0 }}>
              Bu saatler ders saatlerine dahil edilmez, otomatik atlanır.
            </p>
          </div>
          <button className="btn btn-outline btn-sm" onClick={tenefusEkle} style={{ width: isMobile ? '100%' : 'auto', justifyContent: 'center' }}>+ Ara Ekle</button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {tenefusler.map((t, i) => (
            <div key={i} style={{
              display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 130px 120px 120px 36px',
              gap: 10, alignItems: 'center',
              background: t.tip === 'ogle' ? '#FFF7ED' : 'var(--bg-elevated)',
              padding: '10px 12px', borderRadius: 10,
              border: `1px solid ${t.tip === 'ogle' ? '#FED7AA' : 'var(--border)'}`
            }}>
              <input className="form-input" value={t.ad} placeholder="Tenefüs Adı"
                onChange={e => tenefusGuncelle(i, 'ad', e.target.value)} style={{ fontSize: '0.85rem', gridColumn: isMobile ? '1 / -1' : 'auto' }} />
              <select className="form-select" value={t.tip} onChange={e => tenefusGuncelle(i, 'tip', e.target.value)} style={{ fontSize: '0.85rem' }}>
                <option value="tenefus">☕ Tenefüs</option>
                <option value="ogle">🍽 Öğle Arası</option>
              </select>
              <input type="time" className="form-input" value={t.bas}
                onChange={e => tenefusGuncelle(i, 'bas', e.target.value)} style={{ fontSize: '0.85rem' }} />
              <input type="time" className="form-input" value={t.bit}
                onChange={e => tenefusGuncelle(i, 'bit', e.target.value)} style={{ fontSize: '0.85rem' }} />
              <button onClick={() => tenefusSil(i)}
                style={{ background: '#FFF1F2', color: '#EF4444', border: '1px solid #FECACA', borderRadius: 8, cursor: 'pointer', width: isMobile ? '100%' : 36, height: 36, fontSize: '1rem', gridColumn: isMobile ? '1 / -1' : 'auto' }}>
                ×
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// ADIM 2: SINIF & DERS AYARLARI - Üstte kalan slot özeti
// ══════════════════════════════════════════════════════════════════════════════
function Adim2SinifDers({ siniflar, dersler, ayarlar, isMobile = false }) {
  const [seciliSinif, setSeciliSinif] = useState('');
  const [ihtiyaclar, setIhtiyaclar] = useState([]);
  const [form, setForm] = useState({ ders_id: '', haftalik_saat: 2 });
  const [topluHedef, setTopluHedef] = useState('5');
  const [topluTur, setTopluTur] = useState('');
  const [topluKayit, setTopluKayit] = useState(false);

  // Haftalık toplam slot sayısı
  const gunSlotlari = tumGunlerIcinSaatler(ayarlar.gunSaatleri, ayarlar.dersSuresiDk, ayarlar.tenefusler);
  const aktifGunler = getAktifGunler(ayarlar.gunSaatleri);
  const toplamHaftaSlot = aktifGunler.reduce((acc, gun) => acc + (gunSlotlari[gun]?.length || 0), 0);
  const kullanilanSaat = ihtiyaclar.reduce((acc, iht) => acc + (iht.haftalik_saat || 0), 0);
  const kalanSlot = toplamHaftaSlot - kullanilanSaat;

  const seciliSinifObj = siniflar.find(s => s.id.toString() === seciliSinif.toString());
  const topluHedefMeta = TOPLU_MUFREDAT_HEDEFLERI.find(item => item.id === topluHedef) || TOPLU_MUFREDAT_HEDEFLERI[0];
  const hedefBazSiniflar = siniflar.filter(s => sinifHedefeUyuyorMu(s, topluHedef));
  const hedefTurleri = Array.from(new Set(
    hedefBazSiniflar
      .map(s => (s.tur || '').toString().trim())
      .filter(Boolean)
  ));
  const hedefSiniflar = hedefBazSiniflar.filter(s => sinifHedefeUyuyorMu(s, topluHedef, topluTur));

  useEffect(() => {
    if (seciliSinif) {
      axios.get(`/siniflar/${seciliSinif}/ihtiyaclar`).then(r => setIhtiyaclar(r.data.data || []));
    }
  }, [seciliSinif]);

  useEffect(() => {
    if (hedefTurleri.length === 0) {
      setTopluTur('');
      return;
    }
    if (!topluHedefMeta.turSecimi) {
      setTopluTur('');
      return;
    }
    if (!topluTur || !hedefTurleri.some(t => temizMetinDegeri(t) === temizMetinDegeri(topluTur))) {
      setTopluTur(hedefTurleri[0]);
    }
  }, [topluHedef, topluHedefMeta.turSecimi, hedefTurleri, topluTur]);

  const handleEkle = async () => {
    if (!form.ders_id) return toast.error('Ders seçin');
    if (!seciliSinif) return toast.error('Sınıf seçin');
    await axios.post(`/siniflar/${seciliSinif}/ihtiyaclar`, form);
    toast.success('Ders eklendi');
    const r = await axios.get(`/siniflar/${seciliSinif}/ihtiyaclar`);
    setIhtiyaclar(r.data.data || []);
  };

  const handleSil = async (ihtiyacId) => {
    if (!window.confirm('Bu ders ihtiyacını müfredattan kaldırmak istiyor musunuz?')) return;
    try {
      const response = await axios.delete(`/siniflar/ihtiyaclar/${ihtiyacId}`);
      if (response.data.success) {
        toast.success('Ders müfredattan kaldırıldı');
        const r = await axios.get(`/siniflar/${seciliSinif}/ihtiyaclar`);
        setIhtiyaclar(r.data.data || []);
      }
    } catch (err) {
      toast.error('Ders silinemedi.');
    }
  };

  const handleTopluUygula = async () => {
    if (!seciliSinifObj) return toast.error('Önce şablon alınacak sınıfı seçin');
    if (ihtiyaclar.length === 0) return toast.error('Şablon olarak uygulanacak ders dağılımı boş');
    if (hedefSiniflar.length === 0) return toast.error('Seçilen hedefte sınıf bulunamadı');

    const payload = {
      ihtiyaclar: ihtiyaclar.map(iht => ({
        ders_id: iht.ders_id,
        haftalik_saat: iht.haftalik_saat,
      })),
    };

    const hedefAdi = topluHedefMeta.turSecimi && topluTur
      ? `${topluHedefMeta.label} / ${topluTur}`
      : topluHedefMeta.label;

    if (!window.confirm(`"${seciliSinifObj.ad}" ders dağılımı ${hedefSiniflar.length} sınıfa uygulanacak. Devam edilsin mi?`)) {
      return;
    }

    setTopluKayit(true);
    try {
      await Promise.all(
        hedefSiniflar.map(sinif =>
          axios.put(`/siniflar/${sinif.id}/ihtiyaclar`, payload)
        )
      );
      toast.success(`${hedefAdi} için ${hedefSiniflar.length} sınıf güncellendi`);
      if (seciliSinif) {
        const r = await axios.get(`/siniflar/${seciliSinif}/ihtiyaclar`);
        setIhtiyaclar(r.data.data || []);
      }
    } catch (err) {
      toast.error('Toplu uygulama sırasında hata oluştu');
    }
    setTopluKayit(false);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 12 : 16, width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
      {/* Üst Özet: Haftalık slot durumu */}
      <div style={{
        display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(4, 1fr)', gap: 12,
        background: 'var(--bg-elevated)', borderRadius: 14, padding: 16
      }}>
        {aktifGunler.map(gun => {
          const slotSayisi = gunSlotlari[gun]?.length || 0;
          const ayar = ayarlar.gunSaatleri[gun];
          return (
            <div key={gun} style={{
              background: 'var(--bg-surface)', borderRadius: 10, padding: '10px 14px',
              border: '1px solid var(--border)', textAlign: 'center'
            }}>
              <div style={{ fontWeight: 800, fontFamily: 'var(--font-ui)', color: 'var(--accent)', fontSize: '0.82rem' }}>
                {GUN_KISA[gun]}
              </div>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 2 }}>
                {ayar.bas} – {ayar.bit}
              </div>
              <div style={{ fontFamily: 'var(--font-ui)', fontWeight: 900, fontSize: '1.4rem', color: 'var(--text-primary)', lineHeight: 1, marginTop: 4 }}>
                {slotSayisi}
              </div>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>slot</div>
            </div>
          );
        })}
        <div style={{
          background: kalanSlot < 0 ? '#FFF1F2' : 'var(--accent-dim)',
          borderRadius: 10, padding: '10px 14px',
          border: `1px solid ${kalanSlot < 0 ? '#FECACA' : 'var(--accent)'}`,
          textAlign: 'center', gridColumn: aktifGunler.length >= 4 ? 'auto' : '1 / -1'
        }}>
          <div style={{ fontWeight: 800, fontFamily: 'var(--font-ui)', color: kalanSlot < 0 ? '#EF4444' : 'var(--accent)', fontSize: '0.82rem' }}>
            Haftalık Durum
          </div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 2 }}>
            Toplam {toplamHaftaSlot} slot
          </div>
          <div style={{ fontFamily: 'var(--font-ui)', fontWeight: 900, fontSize: '1.4rem', color: kalanSlot < 0 ? '#EF4444' : 'var(--accent)', lineHeight: 1, marginTop: 4 }}>
            {seciliSinif ? (kalanSlot < 0 ? `${Math.abs(kalanSlot)} fazla` : `${kalanSlot} kalan`) : '—'}
          </div>
          {seciliSinif && (
            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
              {kullanilanSaat} atanmış
            </div>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexDirection: isMobile ? 'column' : 'row' }}>
        {/* Sol: Sınıf Seçimi */}
        <div style={{ width: isMobile ? '100%' : 220, flexShrink: 0 }}>
          <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 10 }}>Sınıf Seçin</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {siniflar.map(s => (
              <button key={s.id}
                onClick={() => setSeciliSinif(s.id.toString())}
                style={{
                  padding: '10px 12px', borderRadius: 10, border: '1.5px solid',
                  borderColor: seciliSinif.toString() === s.id.toString() ? 'var(--accent)' : 'var(--border)',
                  background: seciliSinif.toString() === s.id.toString() ? 'var(--accent-dim)' : 'var(--bg-elevated)',
                  color: seciliSinif.toString() === s.id.toString() ? 'var(--accent)' : 'var(--text-secondary)',
                  textAlign: 'left', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600,
                  transition: 'all 0.15s',
                }}
              >
                🏫 {s.ad}
              </button>
            ))}
          </div>
        </div>

        {/* Sağ: Ders İhtiyaçları */}
        <div style={{ flex: 1, width: isMobile ? '100%' : 'auto', minWidth: 0 }}>
          {!seciliSinif ? (
            <div className="empty-state" style={{ padding: 40 }}>
              <p>Soldan bir sınıf seçin</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1.6fr) minmax(300px, 0.9fr)', gap: 16 }}>
              <div className="card" style={{ padding: 20, minWidth: 0 }}>
                <h4 style={{ fontFamily: 'var(--font-ui)', marginBottom: 4 }}>{seciliSinifObj?.ad}</h4>
                <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 16 }}>
                  Haftalık ders dağılımını belirleyin. Toplam atanan: <strong>{kullanilanSaat}</strong> / {toplamHaftaSlot} slot
                </p>

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
                  <span style={{ padding: '6px 10px', borderRadius: 999, background: '#EFF6FF', color: '#2563EB', border: '1px solid #BFDBFE', fontSize: '0.75rem', fontWeight: 800 }}>
                    Seviye: {normalizeSeviye(seciliSinifObj?.seviye || '')}
                  </span>
                  <span style={{ padding: '6px 10px', borderRadius: 999, background: '#F0FDF4', color: '#15803D', border: '1px solid #BBF7D0', fontSize: '0.75rem', fontWeight: 800 }}>
                    Tür: {seciliSinifObj?.tur || '—'}
                  </span>
                  <span style={{ padding: '6px 10px', borderRadius: 999, background: '#FFF7ED', color: '#C2410C', border: '1px solid #FED7AA', fontSize: '0.75rem', fontWeight: 800 }}>
                    Şube: {seciliSinifObj?.sube || '—'}
                  </span>
                </div>

                {/* Doluluk barı */}
                <div style={{ height: 8, background: 'var(--bg-elevated)', borderRadius: 99, marginBottom: 16, overflow: 'hidden' }}>
                  <div style={{
                    height: '100%',
                    width: `${Math.min((kullanilanSaat / Math.max(toplamHaftaSlot, 1)) * 100, 100)}%`,
                    background: kullanilanSaat > toplamHaftaSlot ? '#EF4444' : 'var(--accent)',
                    borderRadius: 99, transition: 'width 0.3s'
                  }} />
                </div>

                <div style={{ display: 'flex', gap: 10, marginBottom: 20, alignItems: 'flex-end', flexDirection: isMobile ? 'column' : 'row' }}>
                  <div className="form-group" style={{ flex: 2, margin: 0 }}>
                    <label className="form-label">Ders</label>
                    <select className="form-select" value={form.ders_id} onChange={e => setForm({ ...form, ders_id: e.target.value })}>
                      <option value="">Seçiniz...</option>
                      {dersler.map(d => <option key={d.id} value={d.id}>{d.ad}</option>)}
                    </select>
                  </div>
                  <div className="form-group" style={{ flex: 1, margin: 0 }}>
                    <label className="form-label">Haftalık Saat</label>
                    <input className="form-input" type="number" min={1} max={20}
                      value={form.haftalik_saat} onChange={e => setForm({ ...form, haftalik_saat: parseInt(e.target.value) || 1 })} />
                  </div>
                  <button className="btn btn-primary" style={{ height: 42, padding: '0 20px', width: isMobile ? '100%' : 'auto', justifyContent: 'center' }} onClick={handleEkle}>Ekle</button>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {ihtiyaclar.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                      Henüz ders eklenmedi
                    </div>
                  ) : ihtiyaclar.map(iht => (
                    <div key={iht.id} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center',
                      flexDirection: isMobile ? 'column' : 'row',
                      padding: '12px 14px', background: 'var(--bg-elevated)', borderRadius: 10,
                      borderLeft: `4px solid ${iht.renk || 'var(--accent)'}`,
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 10, height: 10, borderRadius: '50%', background: iht.renk || 'var(--accent)' }} />
                        <span style={{ fontWeight: 700 }}>{iht.ders_adi}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: isMobile ? 8 : 0 }}>
                        <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                          <strong style={{ color: 'var(--text-primary)' }}>{iht.haftalik_saat}</strong> saat/hafta
                        </span>
                        <button onClick={() => handleSil(iht.id)}
                          style={{ background: '#FFF1F2', color: '#EF4444', border: '1px solid #FECACA', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: '0.75rem' }}>
                          Sil
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="card" style={{ padding: 20, border: '1px solid #D1FAE5', background: 'linear-gradient(180deg, #F0FDF4 0%, #FFFFFF 100%)', minWidth: 0 }}>
                <h4 style={{ fontFamily: 'var(--font-ui)', margin: '0 0 4px 0', color: '#15803D' }}>Toplu Müfredat Uygula</h4>
                <p style={{ fontSize: '0.8rem', color: '#166534', marginBottom: 16 }}>
                  Seçili sınıfın branş-saat dağılımını aynı kademedeki diğer sınıflara tek seferde uygulayın.
                </p>

                <div className="form-group" style={{ marginBottom: 12 }}>
                  <label className="form-label">Hedef Grup</label>
                  <select className="form-select" value={topluHedef} onChange={e => setTopluHedef(e.target.value)}>
                    {TOPLU_MUFREDAT_HEDEFLERI.map(hedef => (
                      <option key={hedef.id} value={hedef.id}>{hedef.label}</option>
                    ))}
                  </select>
                </div>

                {topluHedefMeta.turSecimi && (
                  <div className="form-group" style={{ marginBottom: 12 }}>
                    <label className="form-label">Alan / Tür</label>
                    <select className="form-select" value={topluTur} onChange={e => setTopluTur(e.target.value)}>
                      {hedefTurleri.map(tur => <option key={tur} value={tur}>{tur}</option>)}
                    </select>
                  </div>
                )}

                <div style={{
                  padding: '12px 14px',
                  borderRadius: 12,
                  background: '#FFFFFF',
                  border: '1px solid #BBF7D0',
                  marginBottom: 14,
                }}>
                  <div style={{ fontSize: '0.76rem', color: '#15803D', fontWeight: 800, marginBottom: 6 }}>
                    Hedefteki Sınıflar ({hedefSiniflar.length})
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {hedefSiniflar.length === 0 ? (
                      <span style={{ fontSize: '0.78rem', color: '#64748B' }}>Bu kriterde aktif sınıf bulunamadı.</span>
                    ) : hedefSiniflar.map(s => (
                      <span key={s.id} style={{
                        padding: '6px 9px',
                        borderRadius: 999,
                        background: s.id.toString() === seciliSinif ? '#DCFCE7' : '#F8FAFC',
                        color: s.id.toString() === seciliSinif ? '#166534' : '#334155',
                        border: `1px solid ${s.id.toString() === seciliSinif ? '#86EFAC' : '#E2E8F0'}`,
                        fontSize: '0.72rem',
                        fontWeight: 700,
                      }}>
                        {s.ad}
                      </span>
                    ))}
                  </div>
                </div>

                <div style={{ padding: '12px 14px', borderRadius: 12, background: '#052E16', color: '#DCFCE7', marginBottom: 14 }}>
                  <div style={{ fontSize: '0.75rem', opacity: 0.85 }}>Şablon Kaynak</div>
                  <div style={{ fontFamily: 'var(--font-ui)', fontWeight: 800, marginTop: 4 }}>
                    {seciliSinifObj?.ad || 'Sınıf seçilmedi'}
                  </div>
                  <div style={{ fontSize: '0.75rem', opacity: 0.8, marginTop: 6 }}>
                    {ihtiyaclar.length} branş, toplam {kullanilanSaat} saat/hafta
                  </div>
                </div>

                <button
                  className="btn btn-primary"
                  onClick={handleTopluUygula}
                  disabled={topluKayit || !seciliSinifObj || hedefSiniflar.length === 0}
                  style={{ width: '100%', justifyContent: 'center', background: '#16A34A', border: 'none' }}
                >
                  <span style={{ whiteSpace: isMobile ? 'normal' : 'nowrap', lineHeight: 1.25 }}>
                    {topluKayit ? 'Uygulanıyor...' : 'Seçili Dağılımı Hedef Gruba Uygula'}
                  </span>
                </button>

                <p style={{ fontSize: '0.74rem', color: '#166534', marginTop: 10, marginBottom: 0 }}>
                  5, 6, 7, 8 için tek seviye; 9-10 için ortak; 11-12-Mezun için alan bazlı şablon uygulayabilirsiniz.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// ADIM 3: ÖĞRETMEN AYARLARI - Saat aralıklı meşgul tablo
// ══════════════════════════════════════════════════════════════════════════════
function Adim3OgretmenAyarlari({ ogretmenler, ayarlar, ogretmenMesgulSaatler, setOgretmenMesgulSaatler, isMobile = false }) {
  const [secili, setSecili] = useState('');
  const [dragMode, setDragMode] = useState(false); // Sürükle ile toplu işaretleme
  const [dragValue, setDragValue] = useState(null); // true=dolu yap, false=boşalt
  const [mobilGun, setMobilGun] = useState('Pazartesi');

  const gunSlotlari = tumGunlerIcinSaatler(ayarlar.gunSaatleri, ayarlar.dersSuresiDk, ayarlar.tenefusler);
  const aktifGunler = getAktifGunler(ayarlar.gunSaatleri);
  const tumSaatler = [];
  const saatSet = new Set();
  aktifGunler.forEach((gun) => {
    (gunSlotlari[gun] || []).forEach((slot) => {
      if (!saatSet.has(slot.bas)) {
        saatSet.add(slot.bas);
        tumSaatler.push(slot);
      }
    });
  });
  tumSaatler.sort((a, b) => dakikaYap(a.bas) - dakikaYap(b.bas));

  const toggle = (ogretmenId, gun, slotKey) => {
    const kod = `${gun}_${slotKey}`;
    setOgretmenMesgulSaatler(prev => {
      const mevcut = prev[ogretmenId] || [];
      return {
        ...prev,
        [ogretmenId]: mevcut.includes(kod) ? mevcut.filter(k => k !== kod) : [...mevcut, kod]
      };
    });
  };

  const setVal = (ogretmenId, gun, slotKey, val) => {
    const kod = `${gun}_${slotKey}`;
    setOgretmenMesgulSaatler(prev => {
      const mevcut = new Set(prev[ogretmenId] || []);
      if (val) mevcut.add(kod); else mevcut.delete(kod);
      return { ...prev, [ogretmenId]: Array.from(mevcut) };
    });
  };

  const mesgulSayisi = (ogretmenId) => {
    const list = ogretmenMesgulSaatler[ogretmenId] || [];
    // Sadece aktif gün/saat setinde kalan (ve legacy key ile eşleşen) kayıtları say.
    return list.filter((kod) => {
      const [gun, bas, bit] = String(kod).split('_');
      if (!gun || !bas) return false;
      const slot = (gunSlotlari[gun] || []).find((s) => s.bas === bas);
      if (!slot) return false;
      if (!bit) return true; // legacy: gun_bas
      return slot.bit === bit;
    }).length;
  };
  const seciliOgr = ogretmenler.find(o => o.id.toString() === secili.toString());
  const mesgulList = ogretmenMesgulSaatler[secili] || [];
  const aktifMobilGun = aktifGunler.includes(mobilGun) ? mobilGun : (aktifGunler[0] || 'Pazartesi');
  const gorunenGunler = isMobile ? [aktifMobilGun] : aktifGunler;

  return (
    <div style={{ display: 'flex', gap: 20, flexDirection: isMobile ? 'column' : 'row', width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
      {/* Öğretmen Listesi */}
      <div style={{ width: isMobile ? '100%' : 230, flexShrink: 0 }}>
        <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 10 }}>Öğretmen Seçin</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {ogretmenler.map(o => {
            const ms = mesgulSayisi(o.id);
            const toplam = aktifGunler.reduce((acc, gun) => acc + (gunSlotlari[gun]?.length || 0), 0);
            return (
              <button key={o.id}
                onClick={() => setSecili(o.id.toString())}
                style={{
                  padding: '10px 12px', borderRadius: 10, border: '1.5px solid',
                  borderColor: secili.toString() === o.id.toString() ? '#8B5CF6' : 'var(--border)',
                  background: secili.toString() === o.id.toString() ? 'rgba(139,92,246,0.1)' : 'var(--bg-elevated)',
                  color: secili.toString() === o.id.toString() ? '#8B5CF6' : 'var(--text-secondary)',
                  textAlign: 'left', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600,
                }}
              >
                <div>👤 {o.ad} {o.soyad}</div>
                <div style={{ fontSize: '0.72rem', opacity: 0.7, marginTop: 2 }}>{o.brans}</div>
                {ms > 0 && (
                  <div style={{ marginTop: 4 }}>
                    <div style={{ height: 4, background: 'var(--border)', borderRadius: 99, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${(ms / Math.max(toplam, 1)) * 100}%`, background: '#EF4444', borderRadius: 99 }} />
                    </div>
                    <div style={{ fontSize: '0.68rem', color: '#EF4444', marginTop: 2 }}>{ms} slot dolu</div>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Sağ: Müsaitlik Tablosu */}
      <div style={{ flex: 1, minWidth: 0, overflowX: 'hidden' }}>
        {!secili ? (
          <div className="empty-state" style={{ padding: 40 }}>
            <p>Soldan öğretmen seçin</p>
          </div>
        ) : (
          <div className="card" style={{ padding: 20 }}>
            {isMobile && (
              <div style={{ display: 'flex', gap: 8, marginBottom: 12, overflowX: 'auto' }}>
                {aktifGunler.map((g) => (
                  <button
                    key={g}
                    className="btn btn-sm"
                    onClick={() => setMobilGun(g)}
                    style={{
                      flex: '0 0 auto',
                      padding: '6px 10px',
                      borderRadius: 999,
                      border: '1px solid var(--border)',
                      background: aktifMobilGun === g ? 'var(--accent)' : 'var(--bg-elevated)',
                      color: aktifMobilGun === g ? '#fff' : 'var(--text-secondary)',
                      fontWeight: 700,
                    }}
                  >
                    {GUN_KISA[g]}
                  </button>
                ))}
              </div>
            )}
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
              <div>
                <h4 style={{ fontFamily: 'var(--font-ui)', margin: '0 0 4px 0' }}>
                  {seciliOgr?.ad} {seciliOgr?.soyad} — Müsaitlik Takvimi
                </h4>
                <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', margin: 0 }}>
                  Öğretmenin <b>dolu</b> olduğu saatleri kırmızıya boyayın. Sürükleyerek çoklu seçim yapabilirsiniz.
                </p>
              </div>
              <button
                className="btn btn-outline btn-sm"
                onClick={() => {
                  setOgretmenMesgulSaatler(prev => ({ ...prev, [secili]: [] }));
                  toast.success('Tüm işaretler temizlendi');
                }}
                style={{ color: '#EF4444', borderColor: '#FECACA' }}
              >
                🗑 Temizle
              </button>
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: isMobile ? '0.74rem' : '0.8rem', userSelect: 'none', tableLayout: isMobile ? 'fixed' : 'auto' }}>
              <thead>
                <tr>
                  <th style={{ padding: isMobile ? '6px 6px' : '8px 10px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', width: isMobile ? 56 : 100, textAlign: 'left' }}>
                    Saat
                  </th>
                  {gorunenGunler.map(g => (
                    <th key={g} style={{ padding: isMobile ? '6px 4px' : '8px 6px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', fontFamily: 'var(--font-ui)', fontWeight: 800, textAlign: 'center' }}>
                      {GUN_KISA[g]}
                      <div style={{ fontSize: '0.65rem', fontWeight: 400, color: 'var(--text-muted)', marginTop: 2 }}>
                        {gunSlotlari[g]?.length || 0} slot
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tumSaatler.map((saat) => (
                  <tr key={saat.bas}>
                    <td style={{ padding: isMobile ? '4px 6px' : '6px 10px', border: '1px solid var(--border)', background: 'var(--bg-surface)', fontSize: isMobile ? '0.68rem' : '0.75rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
                      <div>{saat.bas}</div>
                      <div style={{ color: 'var(--text-muted)', fontWeight: 400, fontSize: isMobile ? '0.62rem' : '0.75rem' }}>{saat.bit}</div>
                    </td>
                    {gorunenGunler.map(gun => {
                      const slot = (gunSlotlari[gun] || []).find((s) => s.bas === saat.bas);
                      if (!slot) {
                        return (
                          <td key={gun} style={{ border: '1px solid var(--border)', background: 'var(--bg-elevated)', opacity: 0.4 }} />
                        );
                      }
                      const slotKey = `${slot.bas}_${slot.bit}`;
                      const kod = `${gun}_${slotKey}`;
                      const legacyKod = `${gun}_${slot.bas}`;
                      const isMesgul = mesgulList.includes(kod) || mesgulList.includes(legacyKod);
                      return (
                        <td key={gun}
                          onMouseDown={() => {
                            const newVal = !isMesgul;
                            setDragValue(newVal);
                            setDragMode(true);
                            setVal(secili, gun, slotKey, newVal);
                          }}
                          onMouseEnter={() => {
                            if (dragMode && dragValue !== null) {
                              setVal(secili, gun, slotKey, dragValue);
                            }
                          }}
                          onMouseUp={() => setDragMode(false)}
                          style={{
                            padding: isMobile ? '4px 2px' : '6px 4px',
                            border: '1px solid var(--border)',
                            background: isMesgul ? '#FEE2E2' : '#DCFCE7',
                            color: isMesgul ? '#EF4444' : '#16A34A',
                            textAlign: 'center',
                            cursor: 'pointer',
                            fontWeight: 700,
                            fontSize: isMobile ? '0.66rem' : '0.75rem',
                            transition: 'background 0.1s',
                          }}
                          title={`${gun} ${slot.bas}–${slot.bit}: ${isMesgul ? 'Dolu' : 'Müsait'}`}
                        >
                          {isMesgul ? '✕' : '✓'}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{ marginTop: 14, display: 'flex', gap: 12, fontSize: '0.74rem', flexWrap: 'wrap', flexDirection: isMobile ? 'column' : 'row' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ display: 'inline-block', width: 14, height: 14, background: '#DCFCE7', border: '1px solid #16A34A', borderRadius: 3 }} />
                <span style={{ color: '#16A34A', fontWeight: 700 }}>Müsait</span> (ders verilebilir)
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ display: 'inline-block', width: 14, height: 14, background: '#FEE2E2', border: '1px solid #EF4444', borderRadius: 3 }} />
                <span style={{ color: '#EF4444', fontWeight: 700 }}>Dolu</span> (ders atanamaz)
              </span>
              <span style={{ color: 'var(--text-muted)' }}>💡 Sürükleyerek çoklu seçim yapabilirsiniz.</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// ADIM 4: PROGRAM ÇİZELGESİ (Gelişmiş Sürükle-Bırak + Ekle + Sil + Export)
// ══════════════════════════════════════════════════════════════════════════════
function ProgramCizelgesi({ program, siniflar, ogretmenler, dersler, ayarlar, ogretmenMesgulSaatler, onRefresh, onSil, isMobile = false }) {
  const [gorunumModu, setGorunumModu] = useState('sinif');
  const [seciliId, setSeciliId] = useState('');
  const [dragItem, setDragItem] = useState(null);
  const [dragOverKey, setDragOverKey] = useState(null);
  const [editModal, setEditModal] = useState(null);
  const [ekleModal, setEkleModal] = useState(null); // { gun, slot }
  const [editForm, setEditForm] = useState({});
  const [ekleForm, setEkleForm] = useState({ ders_id: '', ogretmen_id: '', sinif_id: '', salonno: '' });
  const [mobilGun, setMobilGun] = useState('Pazartesi');

  const gunSlotlari = tumGunlerIcinSaatler(ayarlar.gunSaatleri, ayarlar.dersSuresiDk, ayarlar.tenefusler);
  const aktifGunler = getAktifGunler(ayarlar.gunSaatleri);
  const aktifMobilGun = aktifGunler.includes(mobilGun) ? mobilGun : (aktifGunler[0] || 'Pazartesi');
  const gorunenGunler = isMobile ? [aktifMobilGun] : aktifGunler;

  // Tüm saatler: tüm günlerin saatlerinin birleşimi (tekrarsız)
  const tumSaatler = [];
  const saatSet = new Set();
  aktifGunler.forEach(gun => {
    (gunSlotlari[gun] || []).forEach(s => {
      const key = s.bas;
      if (!saatSet.has(key)) {
        saatSet.add(key);
        tumSaatler.push(s);
      }
    });
  });
  tumSaatler.sort((a, b) => dakikaYap(a.bas) - dakikaYap(b.bas));

  // ── Öğretmen listesini PROGRAM verisinden oluştur - ID garantili eşleşme ──
  // ogretmenler prop'u user_id bazlı olabilir, program.ogretmen_id farklı olabilir.
  // Bu yüzden program'dan unique öğretmenleri çıkarıyoruz, ID her zaman eşleşir.
  const programdakiOgretmenler = React.useMemo(() => {
    const map = new Map();
    program.forEach(p => {
      if (p.ogretmen_id && !map.has(p.ogretmen_id)) {
        map.set(p.ogretmen_id, {
          id: p.ogretmen_id,
          userId: null,
          ad: p.ogretmen_adi || `Öğretmen ${p.ogretmen_id}`,
          soyad: '',
          brans: p.ders_adi || '',
        });
      }
    });
    // ogretmenler prop'undan gerçek user/branş bilgisini ogretmen_id ile eşleştir
    map.forEach((val, key) => {
      const match = ogretmenler.find(o => Number(o.ogretmen_id) === Number(key));
      if (match) {
        map.set(key, { ...val, userId: match.id, brans: match.brans || val.brans });
      }
    });
    return Array.from(map.values()).sort((a, b) => a.ad.localeCompare(b.ad));
  }, [program, ogretmenler]);

  useEffect(() => {
    if (gorunumModu === 'sinif' && siniflar.length > 0 && !seciliId) setSeciliId(siniflar[0].id.toString());
    if (gorunumModu === 'ogretmen' && programdakiOgretmenler.length > 0 && !seciliId) setSeciliId(programdakiOgretmenler[0].id.toString());
  }, [gorunumModu, siniflar, programdakiOgretmenler]);

  const handleModDegistir = (mod) => {
    setGorunumModu(mod);
    setSeciliId('');
    setTimeout(() => {
      if (mod === 'sinif' && siniflar.length > 0) setSeciliId(siniflar[0].id.toString());
      else if (mod === 'ogretmen' && programdakiOgretmenler.length > 0) setSeciliId(programdakiOgretmenler[0].id.toString());
    }, 0);
  };

  // Artık ID garantili eşleştiği için basit filtreleme
  const filtreliProgram = program.filter(p => {
    if (gorunumModu === 'sinif') return p.sinif_id?.toString() === seciliId?.toString();
    return p.ogretmen_id?.toString() === seciliId?.toString();
  });

  const seciliOgretmenObj = programdakiOgretmenler.find(o => o.id?.toString() === seciliId?.toString());

  const seciliOgretmenAdi = seciliOgretmenObj?.ad || '';

  const getMesgulListesiByOgretmen = (ogretmenId) => {
    const ogretmen = programdakiOgretmenler.find(o => Number(o.id) === Number(ogretmenId));
    return ogretmenMesgulSaatler[ogretmenId]
      || ogretmenMesgulSaatler[String(ogretmenId)]
      || ogretmenMesgulSaatler[ogretmen?.userId]
      || ogretmenMesgulSaatler[String(ogretmen?.userId)]
      || [];
  };

  const dersRenk = (ders) => {
    const d = dersler.find(d => d.id === ders.ders_id);
    return d?.renk || RENK_PALETI[ders.ders_id % RENK_PALETI.length];
  };

  // Hücredeki dersi bul
  const hucreDers = (gun, saatBas) =>
    filtreliProgram.find(p => p.gun === gun && p.baslangic_saati === saatBas);

  // O gün için slot var mı?
  const slotVarMi = (gun, saatBas) => (gunSlotlari[gun] || []).some(s => s.bas === saatBas);

  // ── Meşgul saatler: artık seciliId = gerçek ogretmen_id ──
  const isMesgulHucre = (gun, saatBas, saatBit) => {
    if (gorunumModu !== 'ogretmen' || !seciliId) return false;
    const mesgulList = getMesgulListesiByOgretmen(seciliId);
    return mesgulList.includes(`${gun}_${saatBas}_${saatBit}`)
      || mesgulList.includes(`${gun}_${saatBas}`);
  };

  const dersiBuSlotaKoyabilirMi = (ders, gun, slot, ignoreIds = []) => {
    if (!ders || !gun || !slot) return { uygun: false, neden: 'Geçersiz hedef' };

    const ignoreSet = new Set(
      [ders.id, ...(Array.isArray(ignoreIds) ? ignoreIds : [])]
        .map(id => Number(id))
        .filter(Boolean)
    );

    const mesgulList = getMesgulListesiByOgretmen(ders.ogretmen_id);
    if (mesgulList.includes(`${gun}_${slot.bas}_${slot.bit}`) || mesgulList.includes(`${gun}_${slot.bas}`)) {
      return { uygun: false, neden: 'Öğretmen müsait değil' };
    }

    const sinifCakisma = program.find(item =>
      !ignoreSet.has(Number(item.id))
      && Number(item.sinif_id) === Number(ders.sinif_id)
      && item.gun === gun
      && item.baslangic_saati === slot.bas
    );
    if (sinifCakisma) {
      return { uygun: false, neden: 'Sınıfın bu saatte dersi var' };
    }

    const ogretmenCakisma = program.find(item =>
      !ignoreSet.has(Number(item.id))
      && Number(item.ogretmen_id) === Number(ders.ogretmen_id)
      && item.gun === gun
      && item.baslangic_saati === slot.bas
    );
    if (ogretmenCakisma) {
      return { uygun: false, neden: 'Öğretmenin bu saatte dersi var' };
    }

    return { uygun: true, neden: 'Müsait' };
  };

  const getDropDurumu = (gun, slot, hedefDers) => {
    if (!dragItem) return null;

    const ayniHucre = dragItem.gun === gun && dragItem.baslangic_saati === slot.bas;
    if (ayniHucre) {
      return { uygun: true, tip: 'self', mesaj: 'Mevcut konum' };
    }

    const kendiKontrolu = dersiBuSlotaKoyabilirMi(
      dragItem,
      gun,
      slot,
      hedefDers ? [hedefDers.id] : []
    );
    if (!kendiKontrolu.uygun) {
      return { uygun: false, tip: 'blocked', mesaj: kendiKontrolu.neden };
    }

    if (!hedefDers || Number(hedefDers.id) === Number(dragItem.id)) {
      return { uygun: true, tip: 'empty', mesaj: 'Taşınabilir' };
    }

    const tersSlot = {
      bas: dragItem.baslangic_saati,
      bit: dragItem.bitis_saati,
    };
    const hedefKontrolu = dersiBuSlotaKoyabilirMi(
      hedefDers,
      dragItem.gun,
      tersSlot,
      [dragItem.id]
    );

    if (!hedefKontrolu.uygun) {
      return { uygun: false, tip: 'swap-blocked', mesaj: `Yer değişemez: ${hedefKontrolu.neden}` };
    }

    return { uygun: true, tip: 'swap', mesaj: 'Yer değiştirilebilir' };
  };

  // ── PDF Export ──
  const handlePdfIndir = () => {
    const seciliAd = gorunumModu === 'sinif'
      ? (siniflar.find(s => s.id?.toString() === seciliId)?.ad || 'Program')
      : seciliOgretmenAdi || 'Program';

    const printWin = window.open('', '_blank');
    const rows = tumSaatler.map(saat => {
      const cols = aktifGunler.map(gun => {
        const ders = hucreDers(gun, saat.bas);
        if (!slotVarMi(gun, saat.bas)) return `<td style="background:#f5f5f5;"></td>`;
        if (!ders) return `<td></td>`;
        const renk = dersRenk(ders);
        const alt = gorunumModu === 'sinif' ? ders.ogretmen_adi : ders.sinif_adi;
        return `<td style="background:${renk}18;border-left:3px solid ${renk};padding:6px 8px;">
          <strong style="color:${renk};font-size:11px;">${ders.ders_adi}</strong><br/>
          <span style="font-size:10px;color:#666;">${alt || ''}</span>
          ${ders.salonno ? `<br/><span style="font-size:9px;color:#999;">Salon: ${ders.salonno}</span>` : ''}
        </td>`;
      }).join('');
      return `<tr>
        <td style="background:#f8f8f8;font-weight:700;font-size:11px;white-space:nowrap;padding:6px 10px;">
          ${saat.bas}<br/><span style="font-weight:400;color:#888;">${saat.bit}</span>
        </td>
        ${cols}
      </tr>`;
    }).join('');

    const headers = aktifGunler.map(g =>
      `<th style="background:#1e293b;color:#fff;padding:10px;font-size:12px;">${GUN_ETIKETLERI[g]}</th>`
    ).join('');

    printWin.document.write(`<!DOCTYPE html><html><head>
      <meta charset="UTF-8"/>
      <title>${seciliAd} - Ders Programı</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 20px; }
        h2 { color: #1e293b; margin-bottom: 4px; }
        p { color: #64748b; font-size: 13px; margin-bottom: 16px; }
        table { width: 100%; border-collapse: collapse; font-size: 12px; }
        th, td { border: 1px solid #e2e8f0; padding: 8px 6px; vertical-align: middle; }
        @media print { body { padding: 8px; } }
      </style>
    </head><body>
      <h2>${seciliAd} — Haftalık Ders Programı</h2>
      <p>Oluşturma: ${new Date().toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' })}</p>
      <table>
        <thead><tr><th style="background:#1e293b;color:#fff;padding:10px;">Saat</th>${headers}</tr></thead>
        <tbody>${rows}</tbody>
      </table>
    </body></html>`);
    printWin.document.close();
    setTimeout(() => { printWin.print(); }, 400);
  };

  // ── Excel Export ──
  const handleExcelIndir = () => {
    const seciliAd = gorunumModu === 'sinif'
      ? (siniflar.find(s => s.id?.toString() === seciliId)?.ad || 'Program')
      : seciliOgretmenAdi || 'Program';

    // CSV olarak oluştur (Excel uyumlu)
    const header = ['Saat', ...aktifGunler.map(g => GUN_ETIKETLERI[g])].join('\t');
    const rowLines = tumSaatler.map(saat => {
      const cols = aktifGunler.map(gun => {
        if (!slotVarMi(gun, saat.bas)) return '';
        const ders = hucreDers(gun, saat.bas);
        if (!ders) return '';
        const alt = gorunumModu === 'sinif' ? ders.ogretmen_adi : ders.sinif_adi;
        return `${ders.ders_adi} (${alt || ''})${ders.salonno ? ' S:' + ders.salonno : ''}`;
      });
      return [`${saat.bas}-${saat.bit}`, ...cols].join('\t');
    });

    const csv = [header, ...rowLines].join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/tab-separated-values;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${seciliAd}_DersProgrami.xls`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ──── Sürükle-Bırak ────
  const handleDragStart = (e, ders) => {
    setDragItem(ders);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDrop = async (e, gun, slot) => {
    e.preventDefault();
    if (!dragItem) return;
    setDragOverKey(null);

    const hedefDers = hucreDers(gun, slot.bas);
    const dropDurumu = getDropDurumu(gun, slot, hedefDers);

    if (!dropDurumu?.uygun) {
      toast.error(dropDurumu?.mesaj || 'Bu alana taşınamaz');
      setDragItem(null);
      return;
    }

    try {
      if (hedefDers && hedefDers.id !== dragItem.id) {
        // Yer değiştirme: iki dersi birbiriyle değiştir
        await Promise.all([
          axios.patch(`/ders-programi/${dragItem.id}/tasi`, {
            gun,
            baslangic_saati: slot.bas,
            bitis_saati: slot.bit,
            ignore_ids: [hedefDers.id],
          }),
          axios.patch(`/ders-programi/${hedefDers.id}/tasi`, {
            gun: dragItem.gun,
            baslangic_saati: dragItem.baslangic_saati,
            bitis_saati: dragItem.bitis_saati,
            ignore_ids: [dragItem.id],
          }),
        ]);
        toast.success('Dersler yer değiştirdi!');
      } else if (!hedefDers) {
        // Boş hücreye taşı
        await axios.patch(`/ders-programi/${dragItem.id}/tasi`, {
          gun,
          baslangic_saati: slot.bas,
          bitis_saati: slot.bit,
        });
        toast.success('Ders taşındı!');
      }
      onRefresh();
    } catch (err) {
      toast.error('Taşıma başarısız');
    }
    setDragItem(null);
  };

  const handleDragOver = (e, gun, saatBas) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverKey(`${gun}_${saatBas}`);
  };

  // ──── Yeni Ders Ekleme Modalı ────
  const openEkle = (gun, slot) => {
    setEkleModal({ gun, slot });
    setEkleForm({
      ders_id: '',
      ogretmen_id: gorunumModu === 'ogretmen' ? seciliId : '',
      sinif_id: gorunumModu === 'sinif' ? seciliId : '',
      salonno: '',
    });
  };

  const handleEkleKaydet = async () => {
    if (!ekleForm.ders_id) return toast.error('Ders seçin');
    if (!ekleForm.ogretmen_id) return toast.error('Öğretmen seçin');
    if (!ekleForm.sinif_id) return toast.error('Sınıf seçin');
    try {
      await axios.post('/ders-programi', {
        ...ekleForm,
        gun: ekleModal.gun,
        baslangic_saati: ekleModal.slot.bas,
        bitis_saati: ekleModal.slot.bit,
      });
      toast.success('Ders eklendi!');
      setEkleModal(null);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Eklenemedi');
    }
  };

  // ──── Düzenleme ────
  const openEdit = (ders) => {
    setEditForm({
      ders_id: ders.ders_id,
      ogretmen_id: ders.ogretmen_id,
      sinif_id: ders.sinif_id,
      gun: ders.gun,
      baslangic_saati: ders.baslangic_saati?.substring(0, 5),
      bitis_saati: ders.bitis_saati?.substring(0, 5),
      salonno: ders.salonno || '',
    });
    setEditModal(ders);
  };

  const handleEditSave = async () => {
    try {
      await axios.put(`/ders-programi/${editModal.id}`, editForm);
      toast.success('Güncellendi!');
      setEditModal(null);
      onRefresh();
    } catch { toast.error('Güncelleme hatası'); }
  };

  return (
    <div style={{ width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
      {/* Üst bar */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, alignItems: 'center', flexWrap: 'wrap', flexDirection: isMobile ? 'column' : 'row', width: '100%' }}>
        <div style={{ display: 'flex', background: 'var(--bg-elevated)', padding: 4, borderRadius: 10, gap: 4, width: isMobile ? '100%' : 'auto', minWidth: 0 }}>
          <button className={`btn ${gorunumModu === 'sinif' ? 'btn-primary' : 'btn-ghost'}`}
            style={{ padding: '7px 16px', flex: isMobile ? 1 : 'unset', justifyContent: 'center' }} onClick={() => handleModDegistir('sinif')}>
            🏫 Sınıf Programı
          </button>
          <button className={`btn ${gorunumModu === 'ogretmen' ? 'btn-primary' : 'btn-ghost'}`}
            style={{ padding: '7px 16px', flex: isMobile ? 1 : 'unset', justifyContent: 'center' }} onClick={() => handleModDegistir('ogretmen')}>
            👤 Öğretmen Programı
          </button>
        </div>

        <select className="form-select" value={seciliId} onChange={e => setSeciliId(e.target.value)}
          style={{ maxWidth: isMobile ? '100%' : 320, width: isMobile ? '100%' : 'auto', fontWeight: 700, minWidth: 0 }}>
          <option value="">-- Seçim yapın --</option>
          {gorunumModu === 'sinif'
            ? siniflar.map(s => <option key={s.id} value={s.id}>{s.ad}</option>)
            : programdakiOgretmenler.map(o => <option key={o.id} value={o.id}>{o.ad} {o.brans ? `(${o.brans})` : ''}</option>)
          }
        </select>

        {/* İndirme Butonları */}
        {seciliId && (
          <div style={{ display: 'flex', gap: 6, marginLeft: isMobile ? 0 : 'auto', width: isMobile ? '100%' : 'auto' }}>
            <button
              onClick={handlePdfIndir}
              className="btn btn-outline btn-sm"
              style={{ color: '#EF4444', borderColor: '#FECACA', fontSize: '0.8rem', gap: 6, flex: isMobile ? 1 : 'unset', justifyContent: 'center' }}
            >
              📄 PDF İndir
            </button>
            <button
              onClick={handleExcelIndir}
              className="btn btn-outline btn-sm"
              style={{ color: '#10B981', borderColor: '#A7F3D0', fontSize: '0.8rem', gap: 6, flex: isMobile ? 1 : 'unset', justifyContent: 'center' }}
            >
              📊 Excel İndir
            </button>
          </div>
        )}

        {!seciliId && (
          <div style={{ marginLeft: isMobile ? 0 : 'auto', fontSize: '0.78rem', color: 'var(--text-muted)', width: isMobile ? '100%' : 'auto' }}>
            💡 Sürükle taşı · Çift tık düzenle · Boş hücreye tıkla ekle
          </div>
        )}
      </div>

      {!seciliId ? (
        <div className="empty-state" style={{ padding: 60 }}>
          <p>Yukarıdan sınıf veya öğretmen seçin</p>
        </div>
      ) : (
        <div style={{ overflowX: 'hidden', width: '100%', maxWidth: '100%' }}
          onMouseUp={() => setDragItem(null)}
        >
          {isMobile && (
            <div style={{ display: 'flex', gap: 8, marginBottom: 10, overflowX: 'auto' }}>
              {aktifGunler.map((g) => (
                <button
                  key={g}
                  className="btn btn-sm"
                  onClick={() => setMobilGun(g)}
                  style={{
                    flex: '0 0 auto',
                    padding: '6px 10px',
                    borderRadius: 999,
                    border: '1px solid var(--border)',
                    background: aktifMobilGun === g ? 'var(--accent)' : 'var(--bg-elevated)',
                    color: aktifMobilGun === g ? '#fff' : 'var(--text-secondary)',
                    fontWeight: 700,
                  }}
                >
                  {GUN_KISA[g]}
                </button>
              ))}
            </div>
          )}
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: isMobile ? '0.72rem' : '0.82rem', tableLayout: isMobile ? 'fixed' : 'auto' }}>
            <thead>
              <tr>
                <th style={{
                  padding: isMobile ? '8px 4px' : '10px 8px', border: '1px solid var(--border)',
                  background: 'var(--bg-elevated)', width: isMobile ? 58 : 110,
                  color: 'var(--text-secondary)', fontFamily: 'var(--font-ui)', textAlign: 'left'
                }}>
                  Saat
                </th>
                {gorunenGunler.map(g => (
                  <th key={g} style={{
                    padding: isMobile ? '8px 4px' : '10px 8px', border: '1px solid var(--border)',
                    background: 'var(--bg-elevated)', fontFamily: 'var(--font-ui)', fontWeight: 800,
                    textAlign: 'center'
                  }}>
                    <div>{isMobile ? GUN_KISA[g] : GUN_ETIKETLERI[g]}</div>
                    <div style={{ display: isMobile ? 'none' : 'block', fontSize: '0.65rem', fontWeight: 400, color: 'var(--text-muted)', marginTop: 2 }}>
                      {ayarlar.gunSaatleri[g]?.bas} – {ayarlar.gunSaatleri[g]?.bit}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tumSaatler.map((saat, sIndex) => (
                <tr key={sIndex}>
                  <td style={{
                    padding: isMobile ? '5px 4px' : '6px 10px', border: '1px solid var(--border)',
                    fontWeight: 700, color: 'var(--text-secondary)',
                    background: 'var(--bg-surface)', whiteSpace: 'nowrap', fontSize: isMobile ? '0.67rem' : '0.78rem'
                  }}>
                    <div>{saat.bas}</div>
                    <div style={{ color: 'var(--text-muted)', fontWeight: 400, fontSize: isMobile ? '0.6rem' : '0.78rem' }}>{saat.bit}</div>
                  </td>
                  {gorunenGunler.map(gun => {
                    const varMi = slotVarMi(gun, saat.bas);
                    const dersHucresi = hucreDers(gun, saat.bas);
                    const isDragOver = dragOverKey === `${gun}_${saat.bas}`;
                    const renk = dersHucresi ? dersRenk(dersHucresi) : null;

                    if (!varMi) {
                      // Bu gün bu saatte slot yok
                      return (
                        <td key={gun} style={{
                          border: '1px solid var(--border)',
                          background: 'repeating-linear-gradient(45deg, transparent, transparent 4px, rgba(0,0,0,0.03) 4px, rgba(0,0,0,0.03) 8px)',
                          opacity: 0.5
                        }} />
                      );
                    }

                    return (
                      <td key={gun}
                        onDragOver={(e) => handleDragOver(e, gun, saat.bas)}
                        onDragLeave={() => setDragOverKey(null)}
                        onDrop={e => handleDrop(e, gun, saat)}
                        onClick={() => !dersHucresi && !isMesgulHucre(gun, saat.bas, saat.bit) && openEkle(gun, saat)}
                        onMouseUp={() => setDragItem(null)}
                        style={{
                          ...(dragItem ? (() => {
                            const dropDurumu = getDropDurumu(gun, saat, dersHucresi);
                            if (!dropDurumu) return {};
                            if (dropDurumu.tip === 'self') {
                              return {
                                outline: '2px solid rgba(59,130,246,0.45)',
                                outlineOffset: -2,
                              };
                            }
                            if (!dropDurumu.uygun) {
                              return {
                                background: 'rgba(15,23,42,0.08)',
                                filter: 'blur(1.6px)',
                                opacity: 0.42,
                              };
                            }
                            return {
                              background: dersHucresi ? 'rgba(16,185,129,0.14)' : 'rgba(16,185,129,0.08)',
                              boxShadow: 'inset 0 0 0 2px rgba(16,185,129,0.28)',
                            };
                          })() : {}),
                          padding: '4px',
                          border: `${isDragOver ? '2px dashed var(--accent)' : '1px solid var(--border)'}`,
                          background: isDragOver
                            ? 'var(--accent-dim)'
                            : isMesgulHucre(gun, saat.bas, saat.bit) && !dersHucresi
                              ? '#FEF2F2'
                              : dersHucresi ? `${renk}12` : 'transparent',
                          verticalAlign: 'middle',
                          height: isMobile ? 64 : 76,
                          transition: 'background 0.1s, border 0.1s',
                          position: 'relative',
                          cursor: dersHucresi ? 'default' : isMesgulHucre(gun, saat.bas, saat.bit) ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {dersHucresi ? (
                          <div
                            draggable
                            onDragStart={e => { e.stopPropagation(); handleDragStart(e, dersHucresi); }}
                            onDragEnd={() => { setDragItem(null); setDragOverKey(null); }}
                            onDoubleClick={() => openEdit(dersHucresi)}
                            style={{
                              background: `${renk}20`,
                              border: `2px solid ${renk}`,
                              borderRadius: 8,
                              padding: isMobile ? '4px 5px' : '5px 8px',
                              cursor: 'grab',
                              userSelect: 'none',
                              height: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'center',
                              gap: isMobile ? 1 : 2,
                              position: 'relative',
                            }}
                          >
                            <div style={{ fontWeight: 800, color: renk, fontSize: isMobile ? '0.66rem' : '0.82rem', lineHeight: 1.2 }}>
                              {dersHucresi.ders_adi}
                            </div>
                            <div style={{ fontSize: isMobile ? '0.56rem' : '0.68rem', color: 'var(--text-muted)', fontWeight: 600 }}>
                              {gorunumModu === 'sinif' ? dersHucresi.ogretmen_adi : dersHucresi.sinif_adi}
                            </div>
                            {dersHucresi.salonno && (
                              <div style={{ fontSize: isMobile ? '0.54rem' : '0.62rem', color: 'var(--text-muted)' }}>
                                Salon: {dersHucresi.salonno}
                              </div>
                            )}
                            <button
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onSil(dersHucresi.id); }}
                              style={{
                                position: 'absolute', top: 2, right: 2,
                                background: '#EF4444', color: '#fff', border: 'none',
                                borderRadius: 4, width: isMobile ? 16 : 18, height: isMobile ? 16 : 18, fontSize: isMobile ? '0.58rem' : '0.65rem',
                                cursor: 'pointer', display: 'flex', alignItems: 'center',
                                justifyContent: 'center', zIndex: 10
                              }}
                            >✕</button>
                          </div>
                        ) : (
                          <div style={{
                            height: '100%', display: 'flex', alignItems: 'center',
                            justifyContent: 'center', flexDirection: 'column', gap: 2,
                            opacity: isDragOver ? 0 : 1
                          }}>
                            {dragItem ? (
                              (() => {
                                const dropDurumu = getDropDurumu(gun, saat, dersHucresi);
                                if (!dropDurumu) return null;
                                return (
                                  <>
                                    <span style={{
                                      fontSize: '0.7rem',
                                      color: dropDurumu.uygun ? '#059669' : '#B91C1C',
                                      fontWeight: 800,
                                      opacity: 0.9,
                                      textAlign: 'center',
                                    }}>
                                      {dropDurumu.uygun ? 'Müsait' : 'Uygun değil'}
                                    </span>
                                    <span style={{
                                      fontSize: '0.62rem',
                                      color: dropDurumu.uygun ? '#047857' : '#991B1B',
                                      opacity: 0.85,
                                      textAlign: 'center',
                                      lineHeight: 1.2,
                                    }}>
                                      {dropDurumu.mesaj}
                                    </span>
                                  </>
                                );
                              })()
                            ) : isMesgulHucre(gun, saat.bas, saat.bit) ? (
                              <span style={{ fontSize: '0.7rem', color: '#EF4444', fontWeight: 700, opacity: 0.7 }}>⛔ Dolu</span>
                            ) : (
                              <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', opacity: 0.6 }}>+ ekle</span>
                            )}
                          </div>
                        )}
                        {dragItem && dersHucresi && (() => {
                          const dropDurumu = getDropDurumu(gun, saat, dersHucresi);
                          if (!dropDurumu || dropDurumu.tip === 'self') return null;
                          return (
                            <div style={{
                              position: 'absolute',
                              inset: 6,
                              borderRadius: 8,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              textAlign: 'center',
                              padding: 8,
                              background: dropDurumu.uygun ? 'rgba(16,185,129,0.16)' : 'rgba(127,29,29,0.18)',
                              color: dropDurumu.uygun ? '#065F46' : '#991B1B',
                              fontSize: '0.64rem',
                              fontWeight: 800,
                              backdropFilter: dropDurumu.uygun ? 'blur(0px)' : 'blur(3px)',
                              pointerEvents: 'none',
                            }}>
                              {dropDurumu.uygun ? 'Yer değiştir' : dropDurumu.mesaj}
                            </div>
                          );
                        })()}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Ders Ekleme Modalı */}
      {ekleModal && (
        <div className="modal-backdrop" onClick={() => setEkleModal(null)}>
          <div className="modal" style={{ maxWidth: 460 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">➕ Ders Ekle</div>
              <button className="modal-close" onClick={() => setEkleModal(null)}>✕</button>
            </div>
            <div style={{ marginBottom: 16, padding: '10px 14px', background: 'var(--accent-dim)', borderRadius: 10, fontSize: '0.85rem', color: 'var(--accent)', fontWeight: 700 }}>
              📅 {GUN_ETIKETLERI[ekleModal.gun]} · {ekleModal.slot.bas} – {ekleModal.slot.bit}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div className="form-group">
                <label className="form-label">Ders *</label>
                <select className="form-select" value={ekleForm.ders_id}
                  onChange={e => setEkleForm({ ...ekleForm, ders_id: parseInt(e.target.value) })}>
                  <option value="">Seçiniz...</option>
                  {dersler.map(d => <option key={d.id} value={d.id}>{d.ad}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Öğretmen *</label>
                <select className="form-select" value={ekleForm.ogretmen_id}
                  onChange={e => setEkleForm({ ...ekleForm, ogretmen_id: parseInt(e.target.value) })}>
                  <option value="">Seçiniz...</option>
                  {ogretmenler.map(o => <option key={o.id} value={o.id}>{o.ad} {o.soyad} – {o.brans}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Sınıf *</label>
                <select className="form-select" value={ekleForm.sinif_id}
                  onChange={e => setEkleForm({ ...ekleForm, sinif_id: parseInt(e.target.value) })}>
                  <option value="">Seçiniz...</option>
                  {siniflar.map(s => <option key={s.id} value={s.id}>{s.ad}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Salon No (opsiyonel)</label>
                <input className="form-input" placeholder="örn: A-12" value={ekleForm.salonno}
                  onChange={e => setEkleForm({ ...ekleForm, salonno: e.target.value })} />
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button className="btn btn-primary" style={{ flex: 1, justifyContent: 'center' }} onClick={handleEkleKaydet}>✓ Ekle</button>
                <button className="btn btn-outline" onClick={() => setEkleModal(null)}>İptal</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editModal && (
        <div className="modal-backdrop" onClick={() => setEditModal(null)}>
          <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">✏️ Ders Düzenle</div>
              <button className="modal-close" onClick={() => setEditModal(null)}>✕</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div className="form-group">
                <label className="form-label">Ders</label>
                <select className="form-select" value={editForm.ders_id}
                  onChange={e => setEditForm({ ...editForm, ders_id: parseInt(e.target.value) })}>
                  {dersler.map(d => <option key={d.id} value={d.id}>{d.ad}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Öğretmen</label>
                <select className="form-select" value={editForm.ogretmen_id}
                  onChange={e => setEditForm({ ...editForm, ogretmen_id: parseInt(e.target.value) })}>
                  {ogretmenler.map(o => <option key={o.id} value={o.id}>{o.ad} {o.soyad}</option>)}
                </select>
              </div>
              {/* Sınıf düzenlenemez - sadece bilgi olarak gösterilir */}
              <div className="form-group">
                <label className="form-label">Sınıf</label>
                <div style={{
                  padding: '10px 14px', background: 'var(--bg-elevated)', borderRadius: 10,
                  border: '1.5px solid var(--border)', fontSize: '0.9rem', fontWeight: 700,
                  color: 'var(--text-secondary)'
                }}>
                  🏫 {siniflar.find(s => s.id?.toString() === editModal?.sinif_id?.toString())?.ad || '—'}
                  <span style={{ fontSize: '0.72rem', fontWeight: 400, color: 'var(--text-muted)', marginLeft: 8 }}>
                    (değiştirilemez)
                  </span>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">Gün</label>
                  <select className="form-select" value={editForm.gun}
                    onChange={e => setEditForm({ ...editForm, gun: e.target.value })}>
                    {TUM_GUNLER.map(g => <option key={g} value={g}>{GUN_ETIKETLERI[g]}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Başlangıç</label>
                  <input type="time" className="form-input" value={editForm.baslangic_saati}
                    onChange={e => setEditForm({ ...editForm, baslangic_saati: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Bitiş</label>
                  <input type="time" className="form-input" value={editForm.bitis_saati}
                    onChange={e => setEditForm({ ...editForm, bitis_saati: e.target.value })} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Salon No (opsiyonel)</label>
                <input className="form-input" placeholder="örn: A-12" value={editForm.salonno}
                  onChange={e => setEditForm({ ...editForm, salonno: e.target.value })} />
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button className="btn btn-primary" style={{ flex: 1, justifyContent: 'center' }} onClick={handleEditSave}>✓ Kaydet</button>
                <button className="btn btn-outline" onClick={() => setEditModal(null)}>İptal</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TumProgramlariModal({ program, siniflar, ogretmenler, dersler, ayarlar, onRefresh, onSil, onClose }) {
  const [editModal, setEditModal] = useState(null);
  const [ekleModal, setEkleModal] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [ekleForm, setEkleForm] = useState({ ders_id: '', ogretmen_id: '', sinif_id: '', salonno: '' });

  const gunSlotlari = tumGunlerIcinSaatler(ayarlar.gunSaatleri, ayarlar.dersSuresiDk, ayarlar.tenefusler);
  const aktifGunler = getAktifGunler(ayarlar.gunSaatleri);
  const tumSaatler = [];
  const saatSet = new Set();

  aktifGunler.forEach(gun => {
    (gunSlotlari[gun] || []).forEach(s => {
      if (!saatSet.has(s.bas)) {
        saatSet.add(s.bas);
        tumSaatler.push(s);
      }
    });
  });
  tumSaatler.sort((a, b) => dakikaYap(a.bas) - dakikaYap(b.bas));

  const sinifProgramMap = React.useMemo(() => {
    const map = new Map();
    program.forEach(item => {
      const key = `${item.sinif_id}_${item.gun}_${item.baslangic_saati}`;
      map.set(key, item);
    });
    return map;
  }, [program]);

  const slotVarMi = (gun, saatBas) => (gunSlotlari[gun] || []).some(s => s.bas === saatBas);
  const dersRenk = (ders) => {
    const d = dersler.find(item => item.id === ders.ders_id);
    return d?.renk || RENK_PALETI[ders.ders_id % RENK_PALETI.length];
  };

  const openEkle = (sinifId, gun, slot) => {
    setEkleModal({ sinifId, gun, slot });
    setEkleForm({ ders_id: '', ogretmen_id: '', sinif_id: sinifId, salonno: '' });
  };

  const openEdit = (ders) => {
    setEditForm({
      ders_id: ders.ders_id,
      ogretmen_id: ders.ogretmen_id,
      sinif_id: ders.sinif_id,
      gun: ders.gun,
      baslangic_saati: ders.baslangic_saati?.substring(0, 5),
      bitis_saati: ders.bitis_saati?.substring(0, 5),
      salonno: ders.salonno || '',
    });
    setEditModal(ders);
  };

  const handleEkleKaydet = async () => {
    if (!ekleForm.ders_id || !ekleForm.ogretmen_id || !ekleForm.sinif_id) {
      return toast.error('Ders, öğretmen ve sınıf seçin');
    }
    try {
      await axios.post('/ders-programi', {
        ...ekleForm,
        gun: ekleModal.gun,
        baslangic_saati: ekleModal.slot.bas,
        bitis_saati: ekleModal.slot.bit,
      });
      toast.success('Ders eklendi');
      setEkleModal(null);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Eklenemedi');
    }
  };

  const handleEditSave = async () => {
    try {
      await axios.put(`/ders-programi/${editModal.id}`, editForm);
      toast.success('Güncellendi');
      setEditModal(null);
      onRefresh();
    } catch {
      toast.error('Güncelleme hatası');
    }
  };

  const programliSiniflar = siniflar
    .filter(s => program.some(p => p.sinif_id?.toString() === s.id?.toString()))
    .sort((a, b) => a.ad.localeCompare(b.ad, 'tr'));

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal"
        style={{ maxWidth: '96vw', width: 1500, maxHeight: '92vh', padding: 0, overflow: 'hidden' }}
        onClick={e => e.stopPropagation()}
      >
        <div className="modal-header" style={{ padding: '18px 22px', borderBottom: '1px solid var(--border)' }}>
          <div>
            <div className="modal-title">🥉 Tüm Programı Görüntüle</div>
            <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginTop: 4 }}>
              Tüm sınıfların haftalık çizelgesi. Boş hücreye tıklayarak ekleyebilir, derse çift tıklayarak düzenleyebilirsiniz.
            </div>
          </div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <div style={{ padding: 20, overflow: 'auto', maxHeight: 'calc(92vh - 76px)', background: 'var(--bg-surface)' }}>
          {programliSiniflar.length === 0 ? (
            <div className="empty-state" style={{ padding: 60 }}>
              <p>Gösterilecek program bulunamadı.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              {programliSiniflar.map((sinif) => {
                const sinifProgram = program.filter(p => p.sinif_id?.toString() === sinif.id?.toString());
                return (
                  <div key={sinif.id} className="card" style={{ padding: 18 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, gap: 12, flexWrap: 'wrap' }}>
                      <div>
                        <div style={{ fontFamily: 'var(--font-ui)', fontWeight: 900, color: '#B45309', fontSize: '1rem' }}>{sinif.ad}</div>
                        <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 3 }}>
                          {sinifProgram.length} ders çizelgelendi
                        </div>
                      </div>
                      <div style={{ fontSize: '0.76rem', color: '#B45309', fontWeight: 700, background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: 999, padding: '6px 10px' }}>
                        Tüm hafta görünümü
                      </div>
                    </div>

                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem', tableLayout: 'fixed' }}>
                        <thead>
                          <tr>
                            <th style={{ padding: '10px 8px', border: '1px solid var(--border)', background: '#FFF7ED', minWidth: 90, textAlign: 'left' }}>Saat</th>
                            {aktifGunler.map(g => (
                              <th key={g} style={{ padding: '10px 8px', border: '1px solid var(--border)', background: '#FFF7ED', textAlign: 'center' }}>
                                <div style={{ fontFamily: 'var(--font-ui)', fontWeight: 800 }}>{GUN_ETIKETLERI[g]}</div>
                                <div style={{ fontSize: '0.62rem', color: 'var(--text-muted)', marginTop: 2 }}>
                                  {ayarlar.gunSaatleri[g]?.bas} – {ayarlar.gunSaatleri[g]?.bit}
                                </div>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {tumSaatler.map((saat) => (
                            <tr key={`${sinif.id}_${saat.bas}`}>
                              <td style={{ padding: '6px 8px', border: '1px solid var(--border)', background: '#FFFBF5', whiteSpace: 'nowrap' }}>
                                <div style={{ fontWeight: 700 }}>{saat.bas}</div>
                                <div style={{ color: 'var(--text-muted)', fontSize: '0.68rem' }}>{saat.bit}</div>
                              </td>
                              {aktifGunler.map((gun) => {
                                const varMi = slotVarMi(gun, saat.bas);
                                const ders = sinifProgramMap.get(`${sinif.id}_${gun}_${saat.bas}`);
                                const renk = ders ? dersRenk(ders) : null;

                                if (!varMi) {
                                  return (
                                    <td key={`${sinif.id}_${gun}_${saat.bas}`} style={{
                                      border: '1px solid var(--border)',
                                      background: 'repeating-linear-gradient(45deg, transparent, transparent 4px, rgba(0,0,0,0.03) 4px, rgba(0,0,0,0.03) 8px)',
                                      opacity: 0.5
                                    }} />
                                  );
                                }

                                return (
                                  <td
                                    key={`${sinif.id}_${gun}_${saat.bas}`}
                                    onClick={() => !ders && openEkle(sinif.id, gun, saat)}
                                    style={{
                                      padding: 4,
                                      border: '1px solid var(--border)',
                                      height: 72,
                                      background: ders ? `${renk}12` : 'transparent',
                                      cursor: ders ? 'default' : 'pointer',
                                    }}
                                  >
                                    {ders ? (
                                      <div
                                        onDoubleClick={() => openEdit(ders)}
                                        style={{
                                          background: `${renk}18`,
                                          border: `1.5px solid ${renk}`,
                                          borderRadius: 8,
                                          padding: '5px 7px',
                                          height: '100%',
                                          display: 'flex',
                                          flexDirection: 'column',
                                          justifyContent: 'center',
                                          gap: 2,
                                          position: 'relative',
                                        }}
                                      >
                                        <div style={{ fontWeight: 800, color: renk, fontSize: '0.76rem', lineHeight: 1.2 }}>
                                          {ders.ders_adi}
                                        </div>
                                        <div style={{ fontSize: '0.64rem', color: 'var(--text-muted)', fontWeight: 600 }}>
                                          {ders.ogretmen_adi}
                                        </div>
                                        {ders.salonno && (
                                          <div style={{ fontSize: '0.6rem', color: 'var(--text-muted)' }}>Salon: {ders.salonno}</div>
                                        )}
                                        <button
                                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onSil(ders.id); }}
                                          style={{
                                            position: 'absolute', top: 2, right: 2,
                                            background: '#EF4444', color: '#fff', border: 'none',
                                            borderRadius: 4, width: 18, height: 18, fontSize: '0.65rem',
                                            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center'
                                          }}
                                        >✕</button>
                                      </div>
                                    ) : (
                                      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', opacity: 0.55 }}>
                                        + ekle
                                      </div>
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {ekleModal && (
          <div className="modal-backdrop" onClick={() => setEkleModal(null)}>
            <div className="modal" style={{ maxWidth: 460 }} onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <div className="modal-title">➕ Ders Ekle</div>
                <button className="modal-close" onClick={() => setEkleModal(null)}>✕</button>
              </div>
              <div style={{ marginBottom: 16, padding: '10px 14px', background: '#FFF7ED', borderRadius: 10, fontSize: '0.85rem', color: '#B45309', fontWeight: 700 }}>
                📅 {GUN_ETIKETLERI[ekleModal.gun]} · {ekleModal.slot.bas} – {ekleModal.slot.bit}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div className="form-group">
                  <label className="form-label">Ders *</label>
                  <select className="form-select" value={ekleForm.ders_id} onChange={e => setEkleForm({ ...ekleForm, ders_id: parseInt(e.target.value) })}>
                    <option value="">Seçiniz...</option>
                    {dersler.map(d => <option key={d.id} value={d.id}>{d.ad}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Öğretmen *</label>
                  <select className="form-select" value={ekleForm.ogretmen_id} onChange={e => setEkleForm({ ...ekleForm, ogretmen_id: parseInt(e.target.value) })}>
                    <option value="">Seçiniz...</option>
                    {ogretmenler.map(o => <option key={o.id} value={o.id}>{o.ad} {o.soyad} – {o.brans}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Salon No</label>
                  <input className="form-input" value={ekleForm.salonno} onChange={e => setEkleForm({ ...ekleForm, salonno: e.target.value })} placeholder="örn: A-12" />
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button className="btn btn-primary" style={{ flex: 1, justifyContent: 'center' }} onClick={handleEkleKaydet}>✓ Ekle</button>
                  <button className="btn btn-outline" onClick={() => setEkleModal(null)}>İptal</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {editModal && (
          <div className="modal-backdrop" onClick={() => setEditModal(null)}>
            <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <div className="modal-title">✏️ Ders Düzenle</div>
                <button className="modal-close" onClick={() => setEditModal(null)}>✕</button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div className="form-group">
                  <label className="form-label">Ders</label>
                  <select className="form-select" value={editForm.ders_id} onChange={e => setEditForm({ ...editForm, ders_id: parseInt(e.target.value) })}>
                    {dersler.map(d => <option key={d.id} value={d.id}>{d.ad}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Öğretmen</label>
                  <select className="form-select" value={editForm.ogretmen_id} onChange={e => setEditForm({ ...editForm, ogretmen_id: parseInt(e.target.value) })}>
                    {ogretmenler.map(o => <option key={o.id} value={o.id}>{o.ad} {o.soyad}</option>)}
                  </select>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                  <div className="form-group">
                    <label className="form-label">Gün</label>
                    <select className="form-select" value={editForm.gun} onChange={e => setEditForm({ ...editForm, gun: e.target.value })}>
                      {TUM_GUNLER.map(g => <option key={g} value={g}>{GUN_ETIKETLERI[g]}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Başlangıç</label>
                    <input type="time" className="form-input" value={editForm.baslangic_saati} onChange={e => setEditForm({ ...editForm, baslangic_saati: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Bitiş</label>
                    <input type="time" className="form-input" value={editForm.bitis_saati} onChange={e => setEditForm({ ...editForm, bitis_saati: e.target.value })} />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Salon No</label>
                  <input className="form-input" value={editForm.salonno} onChange={e => setEditForm({ ...editForm, salonno: e.target.value })} />
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button className="btn btn-primary" style={{ flex: 1, justifyContent: 'center' }} onClick={handleEditSave}>✓ Kaydet</button>
                  <button className="btn btn-outline" onClick={() => setEditModal(null)}>İptal</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// ANA SAYFA: DERSPROGRAMİ SİHİRBAZI
// ══════════════════════════════════════════════════════════════════════════════
export function DersProgramiPage() {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 900);
  const [adim, setAdim] = useState(1);
  const [siniflar, setSiniflar] = useState([]);
  const [dersler, setDersler] = useState([]);
  const [ogretmenler, setOgretmenler] = useState([]);
  const [program, setProgram] = useState([]);
  const [conflictReport, setConflictReport] = useState([]);
  const [conflictLog, setConflictLog] = useState([]);
  const [otoLoading, setOtoLoading] = useState(false);
  const [ayarKaydediliyor, setAyarKaydediliyor] = useState(false);
  const [eskiProgramlar, setEskiProgramlar] = useState([]);
  const [showEskiModal, setShowEskiModal] = useState(false);
  const [showTumProgramModal, setShowTumProgramModal] = useState(false);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [eskiProgramKaydediliyor, setEskiProgramKaydediliyor] = useState(false);

  const [ayarlar, setAyarlar] = useState({
    gunSaatleri: { ...VARSAYILAN_GUN_SAATLERI },
    dersSuresiDk: 40,
    tenefusler: VARSAYILAN_TENEFUSLER,
  });

  const [ogretmenMesgulSaatler, setOgretmenMesgulSaatler] = useState({});

  // Aktif günler
  const aktifGunler = getAktifGunler(ayarlar.gunSaatleri);

  // Günden slot saatlerine map (backend için)
  const gunSlotlari = tumGunlerIcinSaatler(ayarlar.gunSaatleri, ayarlar.dersSuresiDk, ayarlar.tenefusler);

  // Tüm saatler (flatten, tekrarsız)
  const tumUniqSaatler = [];
  const saatSetMain = new Set();
  aktifGunler.forEach(gun => {
    (gunSlotlari[gun] || []).forEach(s => {
      if (!saatSetMain.has(s.bas)) { saatSetMain.add(s.bas); tumUniqSaatler.push(s); }
    });
  });
  tumUniqSaatler.sort((a, b) => dakikaYap(a.bas) - dakikaYap(b.bas));

  const loadData = async () => {
    try {
      const [sRes, dRes, oRes, pRes] = await Promise.all([
        axios.get('/siniflar'),
        axios.get('/dersler'),
        axios.get('/ogretmenler-listesi'),
        axios.get('/ders-programi'),
      ]);
      if (sRes.data.success) setSiniflar(sRes.data.data);
      if (dRes.data.success) setDersler(dRes.data.data);
      if (oRes.data.success) setOgretmenler(oRes.data.data);
      if (pRes.data.success) setProgram(pRes.data.data);
    } catch (e) { console.error(e); }
  };

  const loadAyarlar = async () => {
    try {
      const { data } = await axios.get('/program-ayarlari');
      if (data.success && data.data) {
        const d = data.data;
        setAyarlar(prev => ({
          ...prev,
          gunSaatleri: (d.gun_saatleri && Object.keys(d.gun_saatleri).length > 0)
            ? normalizeGunSaatleri(d.gun_saatleri)
            : prev.gunSaatleri,
          tenefusler: d.tenefusler?.length > 0 ? d.tenefusler : prev.tenefusler,
          dersSuresiDk: d.ders_suresi_dk || prev.dersSuresiDk,
        }));
        if (d.ogretmen_mesgul_saatler && typeof d.ogretmen_mesgul_saatler === 'object') {
          setOgretmenMesgulSaatler(d.ogretmen_mesgul_saatler);
        }
      }
    } catch (e) { /* varsayılan kullan */ }
  };

  useEffect(() => {
    loadData();
    loadAyarlar();
    loadEskiProgramlar();
  }, []);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 900);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const buildAyarlarPayload = () => ({
    gun_saatleri: ayarlar.gunSaatleri,
    gunler: aktifGunler,
    ders_saatleri: tumUniqSaatler,
    tenefusler: ayarlar.tenefusler,
    ogretmen_mesgul_saatler: ogretmenMesgulSaatler,
    ders_suresi_dk: ayarlar.dersSuresiDk,
    gun_slotlari: gunSlotlari, // Her gün için ayrı slotlar
    sinif_ders_saatleri: [],
  });

  const handleAyarKaydet = async () => {
    setAyarKaydediliyor(true);
    try {
      await axios.post('/program-ayarlari', buildAyarlarPayload());
      toast.success('Ayarlar kaydedildi ✅');
    } catch { toast.error('Kayıt hatası'); }
    setAyarKaydediliyor(false);
  };

  const loadEskiProgramlar = async () => {
    try {
      const { data } = await axios.get('/eski-ders-programlari');
      if (data.success) setEskiProgramlar(data.data);
    } catch { /* sessiz */ }
  };

  const handleProgramiKaydet = async () => {
    if (program.length === 0) { toast.error('Kaydedilecek program yok'); return; }
    const ad = window.prompt('Program adı (boş bırakırsanız otomatik isim verilir):');
    if (ad === null) return; // iptal
    setEskiProgramKaydediliyor(true);
    try {
      await axios.post('/eski-ders-programlari', { ad: ad || '' });
      toast.success('Program kaydedildi ✅');
      loadEskiProgramlar();
    } catch { toast.error('Kayıt hatası'); }
    setEskiProgramKaydediliyor(false);
  };

  const handleEskiProgramYukle = async (eskiId) => {
    try {
      const { data } = await axios.get(`/eski-ders-programlari/${eskiId}`);
      if (data.success) {
        setProgram(data.data.program_data);
        setShowEskiModal(false);
        setAdim(4);
        toast.success('Eski program yüklendi');
      }
    } catch { toast.error('Yüklenemedi'); }
  };

  const handleOtomatikOlustur = async () => {
    if (!window.confirm('Mevcut program silinerek yeniden oluşturulacak. Devam edilsin mi?')) return;
    setOtoLoading(true);
    setConflictReport([]);
    try {
      await axios.post('/program-ayarlari', buildAyarlarPayload());
      const { data } = await axios.post('/ders-programi/otomatik');
      toast.success(data.message || '✨ Program oluşturuldu!');
      await loadData();
      const yeniConflictler = Array.isArray(data.conflict_report) ? data.conflict_report : [];
      const warningMessages = Array.isArray(data.warning_messages) ? data.warning_messages : [];
      setConflictReport(yeniConflictler);
      setConflictLog(prev => ([
        ...prev,
        ...(warningMessages.length > 0
          ? warningMessages.map((mesaj, index) => ({
            log_id: `${Date.now()}-${index}-${Math.random().toString(36).slice(2, 8)}`,
            created_at: new Date().toISOString(),
            mesaj,
          }))
          : yeniConflictler.map(item => ({
            ...item,
            log_id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            created_at: new Date().toISOString(),
            mesaj: `${item.sinif_adi} için ${item.ders_adi} dersi ${item.eksik_saat} saat eksik yerleştirilebildi`,
          }))),
      ]));
      setAdim(4);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Program oluşturulamadı');
    }
    setOtoLoading(false);
  };

  const handleCizelgeDersSil = async (id) => {
    if (!window.confirm('Bu dersi programdan kaldırmak istiyor musunuz?')) return;
    try {
      const response = await axios.delete(`/ders-programi/${id}`);
      if (response.data.success) {
        toast.success('Ders çizelgeden silindi');
        loadData();
      }
    } catch (err) {
      toast.error('Ders silinemedi.');
    }
  };

  const ADIMLAR = [
    { no: 1, ad: 'Genel Ayarlar', ikon: '⚙️', renk: '#3B82F6' },
    { no: 2, ad: 'Sınıf & Dersler', ikon: '📚', renk: '#10B981' },
    { no: 3, ad: 'Öğretmen Ayarları', ikon: '👤', renk: '#8B5CF6' },
    { no: 4, ad: 'Program Çizelgesi', ikon: '📅', renk: '#EF4444' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 14 : 20, width: '100%', maxWidth: '100vw', overflowX: 'hidden' }}>
      {/* ── Yapay Zeka Yükleme Overlay ── */}
      {otoLoading && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(15,23,42,0.7)', backdropFilter: 'blur(6px)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          gap: 24
        }}>
          <div style={{ position: 'relative', width: 80, height: 80 }}>
            <div style={{
              position: 'absolute', inset: 0, borderRadius: '50%',
              border: '4px solid rgba(139,92,246,0.3)',
              borderTopColor: '#8B5CF6',
              animation: 'spin 1s linear infinite'
            }} />
            <div style={{
              position: 'absolute', inset: 10, borderRadius: '50%',
              border: '4px solid rgba(99,102,241,0.2)',
              borderBottomColor: '#6366F1',
              animation: 'spin 1.5s linear infinite reverse'
            }} />
            <div style={{
              position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
              justifyContent: 'center', fontSize: '1.8rem'
            }}>🧠</div>
          </div>
          <div style={{ textAlign: 'center', color: '#fff' }}>
            <div style={{ fontFamily: 'var(--font-ui)', fontSize: '1.3rem', fontWeight: 900, marginBottom: 8 }}>
              Yapay Zeka Çalışıyor
            </div>
            <div style={{ fontSize: '0.9rem', opacity: 0.7 }}>
              Ders programı oluşturuluyor, lütfen bekleyin...
            </div>
          </div>
        </div>
      )}

      {/* Başlık */}
      <div className="section-header animate-fadeInUp">
        <div>
          <h2 style={{ fontFamily: 'var(--font-ui)', fontSize: '1.4rem', fontWeight: 900 }}>⚙️ Ders Programı Sihirbazı</h2>
          <div className="section-subtitle">Adım adım ders programını yapılandırın ve Yapay Zeka ile otomatik oluşturun</div>
        </div>
      </div>

      {/* Adım Göstergesi */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(4, minmax(0, 1fr))',
          gap: 6,
          background: 'var(--bg-elevated)',
          borderRadius: 14,
          padding: 6,
          width: '100%',
          maxWidth: '100%',
          overflow: 'hidden'
        }}
        className="animate-fadeInUp stagger-1"
      >
        {ADIMLAR.map((a) => (
          <button key={a.no}
            onClick={() => setAdim(a.no)}
            style={{
              width: '100%',
              minWidth: 0,
              padding: isMobile ? '10px 6px' : '12px 8px',
              borderRadius: 10, border: 'none', cursor: 'pointer',
              background: adim === a.no ? a.renk : 'transparent',
              color: adim === a.no ? '#fff' : adim > a.no ? a.renk : 'var(--text-muted)',
              fontWeight: adim === a.no ? 800 : 600,
              fontFamily: 'var(--font-ui)', fontSize: isMobile ? '0.72rem' : '0.82rem', transition: 'all 0.2s',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, whiteSpace: 'nowrap',
              overflow: 'hidden',
            }}
          >
            <span>{a.ikon}</span>
            <span>{a.ad}</span>
            {adim > a.no && (
              <span style={{ marginLeft: 4, fontSize: '0.75rem', background: a.renk + '22', borderRadius: 10, padding: '1px 6px', color: a.renk }}>✓</span>
            )}
          </button>
        ))}
      </div>

      {/* İşlem Butonları */}
      <div
        className="animate-fadeInUp stagger-1"
        style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}
      >
        {adim === 4 && program.length > 0 && (
          <button className="btn btn-outline" onClick={handleProgramiKaydet} disabled={eskiProgramKaydediliyor}
            style={{ borderColor: '#10B981', color: '#10B981' }}>
            {eskiProgramKaydediliyor ? '⏳...' : '📋 Programı Kaydet'}
          </button>
        )}
        {eskiProgramlar.length > 0 && (
          <button className="btn btn-outline" onClick={() => { loadEskiProgramlar(); setShowEskiModal(true); }}
            style={{ borderColor: '#F59E0B', color: '#F59E0B', width: isMobile ? '100%' : 'auto', justifyContent: isMobile ? 'center' : 'flex-start' }}>
            🗂 Eski Programlar ({eskiProgramlar.length})
          </button>
        )}
        {conflictLog.length > 0 && (
          <button
            className="btn btn-outline"
            onClick={() => setShowConflictModal(true)}
            style={{ borderColor: '#F87171', color: '#DC2626', fontWeight: 800, width: isMobile ? '100%' : 'auto', justifyContent: isMobile ? 'center' : 'flex-start' }}
          >
            ⚠ Uyarılar ({conflictLog.length})
          </button>
        )}
        <button
          className="btn"
          style={{
            background: 'linear-gradient(135deg, #B45309, #D97706)',
            color: '#fff',
            border: 'none',
            fontWeight: 800,
            padding: '10px 20px',
            width: isMobile ? '100%' : 'auto',
            justifyContent: isMobile ? 'center' : 'flex-start'
          }}
          onClick={() => setShowTumProgramModal(true)}
          disabled={program.length === 0}
        >
          🥉 Tüm Programı Görüntüle
        </button>
        <button
          className="btn"
          style={{ background: 'linear-gradient(135deg, #6366F1, #8B5CF6)', color: '#fff', border: 'none', fontWeight: 800, padding: '10px 20px', width: isMobile ? '100%' : 'auto', justifyContent: isMobile ? 'center' : 'flex-start' }}
          onClick={handleOtomatikOlustur}
          disabled={otoLoading}
        >
          {otoLoading ? '🧠 AI Çalışıyor...' : '✨ Otomatik Program Oluştur (AI)'}
        </button>
      </div>

      {/* İçerik */}
      <div className="animate-fadeInUp stagger-2">
        {adim === 1 && (
          <Adim1GenelAyarlar ayarlar={ayarlar} setAyarlar={setAyarlar} isMobile={isMobile} />
        )}

        {adim === 2 && (
          <div className="card" style={{ padding: isMobile ? 14 : 24 }}>
            <div style={{ marginBottom: 20 }}>
              <h3 style={{ fontFamily: 'var(--font-ui)', color: '#10B981', margin: 0 }}>📚 Sınıf Ders Dağılımı</h3>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: 4 }}>
                Her sınıf için branş bazlı haftalık saatleri girin; isterseniz seçili dağılımı 5. sınıflar, 9-10 veya 11-12-Mezun alan gruplarına toplu uygulayın.
              </p>
            </div>
            <Adim2SinifDers siniflar={siniflar} dersler={dersler} ayarlar={ayarlar} isMobile={isMobile} />
          </div>
        )}

        {adim === 3 && (
          <div className="card" style={{ padding: isMobile ? 14 : 24 }}>
            <div style={{ marginBottom: 20 }}>
              <h3 style={{ fontFamily: 'var(--font-ui)', color: '#8B5CF6', margin: 0 }}>👤 Öğretmen Müsaitlik Ayarları</h3>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: 4 }}>
                Her öğretmenin ders veremeyeceği saatleri kırmızıyla işaretleyin. Sürükleyerek toplu seçim yapabilirsiniz.
              </p>
            </div>
            <Adim3OgretmenAyarlari
              ogretmenler={ogretmenler}
              ayarlar={ayarlar}
              ogretmenMesgulSaatler={ogretmenMesgulSaatler}
              setOgretmenMesgulSaatler={setOgretmenMesgulSaatler}
              isMobile={isMobile}
            />
          </div>
        )}

        {adim === 4 && (
          <div className="card" style={{ padding: isMobile ? 14 : 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', marginBottom: 20, gap: 12, flexDirection: isMobile ? 'column' : 'row' }}>
              <div>
                <h3 style={{ fontFamily: 'var(--font-ui)', color: '#EF4444', margin: 0 }}>📅 Haftalık Ders Programı</h3>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: 4 }}>
                  <strong>{program.length}</strong> ders çizelgelendi.
                  Sürükle-taşı · Çift tık düzenle · Boş hücreye tıkla ekle
                </p>
              </div>
              <button
                className="btn"
                style={{ background: 'linear-gradient(135deg, #6366F1, #8B5CF6)', color: '#fff', border: 'none', fontWeight: 800, width: isMobile ? '100%' : 'auto', justifyContent: isMobile ? 'center' : 'flex-start' }}
                onClick={handleOtomatikOlustur}
                disabled={otoLoading}
              >
                {otoLoading ? '🧠 Oluşturuluyor...' : '✨ Program Oluştur'}
              </button>
            </div>

            {program.length === 0 ? (
              <div className="empty-state" style={{ padding: 60 }}>
                <div style={{ fontSize: '3rem', marginBottom: 12 }}>🗓</div>
                <h3>Henüz program oluşturulmadı</h3>
                <p>Yukarıdaki "Otomatik Program Oluştur" butonuna tıklayın</p>
              </div>
            ) : (
              <ProgramCizelgesi
                program={program}
                siniflar={siniflar}
                ogretmenler={ogretmenler}
                dersler={dersler}
                ayarlar={ayarlar}
                ogretmenMesgulSaatler={ogretmenMesgulSaatler}
                onRefresh={loadData}
                onSil={handleCizelgeDersSil}
                isMobile={isMobile}
              />
            )}
          </div>
        )}
      </div>

      {/* Alt Navigasyon */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 8, gap: 10, flexDirection: isMobile ? 'column' : 'row' }}>
        <button className="btn btn-outline"
          style={{ visibility: adim === 1 ? 'hidden' : 'visible' }}
          onClick={() => setAdim(a => a - 1)}>
          ← Önceki Adım
        </button>
        <span style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>
          Adım {adim} / {ADIMLAR.length}
        </span>
        <button className="btn btn-primary"
          style={{ visibility: adim === 4 ? 'hidden' : 'visible' }}
          onClick={() => setAdim(a => a + 1)}>
          Sonraki Adım →
        </button>
      </div>

      {/* Eski Programlar Modalı */}
      {showEskiModal && (
        <div className="modal-backdrop" onClick={() => setShowEskiModal(false)}>
          <div className="modal" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">🗂 Kayıtlı Eski Ders Programları</div>
              <button className="modal-close" onClick={() => setShowEskiModal(false)}>✕</button>
            </div>
            <p style={{ fontSize: '0.84rem', color: 'var(--text-muted)', marginBottom: 16 }}>
              Seçtiğiniz program çizelgeye yüklenecek. Mevcut program değiştirilmeyecek.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxHeight: 400, overflowY: 'auto' }}>
              {eskiProgramlar.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)' }}>Kayıtlı program yok</div>
              ) : eskiProgramlar.map(ep => {
                const tarih = new Date(ep.created_at);
                const tarihStr = tarih.toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' });
                const saatGoster = tarih.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
                return (
                  <div key={ep.id} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '14px 16px', background: 'var(--bg-elevated)', borderRadius: 12,
                    border: '1px solid var(--border)'
                  }}>
                    <div>
                      <div style={{ fontWeight: 800, fontSize: '0.9rem', color: 'var(--text-primary)' }}>{ep.ad}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 3 }}>
                        📅 {tarihStr} · 🕐 {saatGoster}
                      </div>
                    </div>
                    <button
                      className="btn btn-primary btn-sm"
                      style={{ padding: '8px 16px', fontSize: '0.8rem' }}
                      onClick={() => handleEskiProgramYukle(ep.id)}
                    >
                      Yükle
                    </button>
                  </div>
                );
              })}
            </div>
            <button className="btn btn-outline" style={{ width: '100%', marginTop: 16, justifyContent: 'center' }}
              onClick={() => setShowEskiModal(false)}>Kapat</button>
          </div>
        </div>
      )}

      {showTumProgramModal && (
        <TumProgramlariModal
          program={program}
          siniflar={siniflar}
          ogretmenler={ogretmenler}
          dersler={dersler}
          ayarlar={ayarlar}
          onRefresh={loadData}
          onSil={handleCizelgeDersSil}
          onClose={() => setShowTumProgramModal(false)}
        />
      )}

      {showConflictModal && (
        <div className="modal-backdrop" onClick={() => setShowConflictModal(false)}>
          <div
            className="modal-card"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 820, width: '100%', padding: 0, overflow: 'hidden' }}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              padding: '18px 22px',
              borderBottom: '1px solid var(--border)',
              background: 'linear-gradient(135deg, rgba(254,242,242,0.96), rgba(255,247,237,0.96))',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{
                  width: 40,
                  height: 40,
                  borderRadius: 14,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: '#FEE2E2',
                  color: '#DC2626',
                  fontSize: '1.2rem',
                  fontWeight: 900,
                }}>
                  !
                </div>
                <div>
                  <div style={{ fontFamily: 'var(--font-ui)', fontWeight: 900, color: '#B91C1C', fontSize: '1.05rem' }}>
                    Eksik Yerleşen Ders Uyarıları
                  </div>
                  <div style={{ color: '#7F1D1D', fontSize: '0.84rem' }}>
                    Toplam {conflictLog.length} uyarı bulundu.
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-outline" onClick={() => setConflictLog([])}>Temizle</button>
                <button className="btn btn-outline" onClick={() => setShowConflictModal(false)}>Kapat</button>
              </div>
            </div>

            <div style={{ padding: 20, maxHeight: '70vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10 }}>
              {conflictLog.map((item, index) => (
                <div
                  key={item.log_id || `${item.sinif_id}-${item.ders_adi}-${index}`}
                  style={{
                    padding: '12px 14px',
                    borderRadius: 12,
                    background: 'rgba(255,255,255,0.96)',
                    border: '1px solid rgba(248,113,113,0.22)',
                    color: '#7F1D1D',
                    fontSize: '0.88rem',
                    lineHeight: 1.4,
                  }}
                >
                  <strong>{index + 1}.</strong> {item.mesaj || `${item.sinif_adi} için ${item.ders_adi} dersi ${item.eksik_saat} saat eksik yerleştirilebildi`}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// SINIF YÖNETİMİ SAYFASI (DEĞIŞMEDEN KORUNDU)
// ══════════════════════════════════════════════════════════════════════════════
export function SiniflarPage() {
  const navigate = useNavigate();
  const [siniflar, setSiniflar] = useState([]);
  const [dersler, setDersler] = useState([]);
  const [loading, setLoading] = useState(true);
  const [arama, setArama] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showEdit, setShowEdit] = useState(null);

  const [showIhtiyac, setShowIhtiyac] = useState(null);
  const [ihtiyaclar, setIhtiyaclar] = useState([]);
  const [ihtiyacForm, setIhtiyacForm] = useState({ ders_id: '', haftalik_saat: 2 });
  const [ihtiyacTab, setIhtiyacTab] = useState('mufredat'); // 'mufredat' | 'eskiProgram'
  const [eskiProgramlar, setEskiProgramlar] = useState([]);
  const [eskiProgramDetay, setEskiProgramDetay] = useState(null);
  const [seciliEskiId, setSeciliEskiId] = useState(null);

  const KADEMELER = {
    'Ortaokul': { seviyeler: ['5', '6', '7', '8'] },
    'Lise / Mezun': { seviyeler: ['9', '10', '11', '12', 'Mezun'] },
    'Diğer': { seviyeler: ['Genel', 'Yetişkin', 'Çocuk'] }
  };

  const getTurler = (seviye) => {
    if (['11', '12', 'Mezun'].includes(seviye)) return ['Sayısal', 'Eşit Ağırlık', 'Sözel', 'Dil'];
    if (['7', '8'].includes(seviye)) return ['LGS', 'Okula Destek'];
    if (['5', '6', '9', '10'].includes(seviye)) return ['Okula Destek'];
    return ['Genel'];
  };

  const [secilenKademe, setSecilenKademe] = useState('Lise / Mezun');
  const [form, setForm] = useState({ ad: '', seviye: '11', sube: 'A', tur: 'Sayısal', kapasite: 30 });
  const [editForm, setEditForm] = useState({});

  const handleKademeChange = (k) => {
    setSecilenKademe(k);
    const ilkSeviye = KADEMELER[k].seviyeler[0];
    const ilkTur = getTurler(ilkSeviye)[0];
    setForm(prev => ({ ...prev, seviye: ilkSeviye, tur: ilkTur }));
  };

  const handleSeviyeChange = (e) => {
    const s = e.target.value;
    const ilkTur = getTurler(s)[0];
    setForm({ ...form, seviye: s, tur: ilkTur });
  };

  useEffect(() => {
    if (showModal) {
      let otoAd = `${form.seviye}. Sınıf ${form.tur} - ${form.sube} Şubesi`;
      if (form.seviye === 'Mezun') otoAd = `Mezun ${form.tur} - ${form.sube} Şubesi`;
      if (['Genel', 'Yetişkin', 'Çocuk'].includes(form.seviye)) {
        otoAd = `${form.seviye} ${form.tur} - ${form.sube} Grubu`;
      }
      setForm(prev => ({ ...prev, ad: otoAd }));
    }
  }, [form.seviye, form.sube, form.tur, showModal]);

  const load = async () => {
    try {
      const [sinifRes, dersRes] = await Promise.all([axios.get('/siniflar'), axios.get('/dersler')]);
      if (sinifRes.data.success) setSiniflar(sinifRes.data.data);
      if (dersRes.data.success) setDersler(dersRes.data.data);
    } catch { } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    if (!form.ad || !form.sube) { toast.error('Sınıf adı ve şube zorunludur'); return; }
    try {
      await axios.post('/siniflar', form);
      toast.success('Sınıf başarıyla oluşturuldu! 🚀');
      setShowModal(false); load();
    } catch (e) { toast.error('Kayıt Hatası'); }
  };

  const handleEdit = async () => {
    try {
      await axios.put(`/siniflar/${showEdit}`, editForm);
      toast.success('Sınıf başarıyla güncellendi! ✅');
      setShowEdit(null);
      load();
    } catch { toast.error('Güncelleme hatası'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu sınıfı silmek istiyor musunuz?')) return;
    try { await axios.delete(`/siniflar/${id}`); toast.success('Sınıf silindi'); load(); }
    catch { toast.error('Silme hatası. (Yetkiniz olmayabilir)'); }
  };

  const openIhtiyacModal = async (sinif) => {
    setShowIhtiyac(sinif);
    setIhtiyacTab('mufredat');
    setEskiProgramDetay(null);
    setSeciliEskiId(null);
    try {
      const { data } = await axios.get(`/siniflar/${sinif.id}/ihtiyaclar`);
      if (data.success) setIhtiyaclar(data.data);
    } catch { toast.error('Müfredat yüklenemedi'); }
    // Eski programları yükle
    try {
      const { data } = await axios.get('/eski-ders-programlari');
      if (data.success) setEskiProgramlar(data.data);
    } catch { /* sessiz */ }
  };

  const handleEskiProgramSec = async (eskiId) => {
    setSeciliEskiId(eskiId);
    try {
      const { data } = await axios.get(`/eski-ders-programlari/${eskiId}?sinif_id=${showIhtiyac.id}`);
      if (data.success) setEskiProgramDetay(data.data.program_data);
    } catch { toast.error('Yüklenemedi'); }
  };

  const handleAddIhtiyac = async () => {
    if (!ihtiyacForm.ders_id) { toast.error('Lütfen bir ders seçin'); return; }
    try {
      await axios.post(`/siniflar/${showIhtiyac.id}/ihtiyaclar`, ihtiyacForm);
      toast.success('Ders ihtiyacı kaydedildi');
      openIhtiyacModal(showIhtiyac);
      setIhtiyacForm({ ders_id: '', haftalik_saat: 2 });
    } catch { toast.error('Kaydedilemedi'); }
  };

  const handleDeleteIhtiyac = async (ihtiyacId) => {
    try {
      await axios.delete(`/siniflar/ihtiyaclar/${ihtiyacId}`);
      openIhtiyacModal(showIhtiyac);
    } catch { toast.error('Silinemedi'); }
  };

  const filtreliSiniflar = siniflar.filter((sinif) => {
    const q = arama.trim().toLowerCase();
    if (!q) return true;
    return [sinif.ad, sinif.seviye, sinif.sube, sinif.tur]
      .some((alan) => (alan || '').toString().toLowerCase().includes(q));
  });

  const getLevelBadge = (seviye = '') => {
    const temiz = seviye.toString().replace('. Sınıf', '').trim();
    return temiz || 'GN';
  };

  const getTurRenk = (tur = '') => {
    const temiz = tur.toLowerCase();
    if (temiz.includes('say')) return { bg: '#EEF2FF', text: '#4338CA', border: '#C7D2FE' };
    if (temiz.includes('eşit') || temiz.includes('esit')) return { bg: '#F5F3FF', text: '#6D28D9', border: '#DDD6FE' };
    if (temiz.includes('lgs')) return { bg: '#EFF6FF', text: '#2563EB', border: '#BFDBFE' };
    return { bg: '#EEF6FF', text: '#1D4ED8', border: '#BFDBFE' };
  };

  const getDolulukRenk = (oran) => {
    if (oran >= 90) return '#EF4444';
    if (oran >= 70) return '#F59E0B';
    return '#3B82F6';
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
      <div className="section-header animate-fadeInUp">
        <div>
          <h2 style={{ fontFamily: 'var(--font-ui)', fontSize: '2.25rem', fontWeight: 900, letterSpacing: '-0.04em' }}>🏫 Sınıf Yönetimi</h2>
          <div className="section-subtitle" style={{ fontSize: '0.98rem', marginTop: 10, color: '#64748B' }}>Toplam {siniflar.length} aktif sınıf kayıtlı</div>
        </div>
        <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', minWidth: 280 }}>
            <input className="form-input" placeholder="Sınıf ara..." value={arama} onChange={e => setArama(e.target.value)}
              style={{ height: 56, borderRadius: 18, paddingLeft: 50, minWidth: 280, background: '#FFFFFF', border: '1.5px solid #D8E1EE', boxShadow: '0 1px 2px rgba(15,23,42,0.02)' }} />
            <span style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', color: '#94A3B8', fontSize: '1.15rem' }}>⌕</span>
          </div>
          <button className="btn btn-primary" style={{ height: 56, borderRadius: 18, padding: '0 28px', boxShadow: '0 10px 24px rgba(232,51,74,0.18)', fontWeight: 800 }} onClick={() => setShowModal(true)}>+ Yeni Sınıf Ekle</button>
        </div>
      </div>

      <div className="animate-fadeInUp stagger-1" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 24 }}>
        {loading ? Array(4).fill(0).map((_, i) => <div key={i} className="skeleton" style={{ height: 320, borderRadius: 28 }} />) :
          filtreliSiniflar.map((s, i) => {
            const oran = Math.min((((s.ogrenci_sayisi || 0) / Math.max(s.kapasite || 1, 1)) * 100), 100);
            const turRenk = getTurRenk(s.tur);
            const progressRenk = getDolulukRenk(oran);
            return (
              <div key={i} className="card" style={{ cursor: 'default', padding: 0, overflow: 'hidden', borderRadius: 24, border: '1px solid #E2E8F0', boxShadow: '0 10px 28px rgba(15,23,42,0.06)', background: '#FFFFFF' }}>
                <div style={{ height: 5, background: '#F87171' }} />
                <div style={{ padding: '22px 22px 18px', display: 'flex', flexDirection: 'column', minHeight: 290 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 14, marginBottom: 18 }}>
                    <div style={{ width: 68, height: 68, borderRadius: 22, background: '#FFF5F5', border: '1px solid #FECACA', color: '#DC2626', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-ui)', fontWeight: 900, fontSize: '1.75rem', boxShadow: '0 6px 16px rgba(248,113,113,0.10)' }}>
                      {getLevelBadge(s.seviye)}
                    </div>
                    <div style={{ display: 'flex', gap: 10 }}>
                      <button className="btn btn-ghost btn-sm" style={{ padding: '6px 8px', color: '#94A3B8' }} onClick={() => { setEditForm({ ...s }); setShowEdit(s.id); }}>✏️</button>
                      <button className="btn btn-ghost btn-sm" style={{ padding: '6px 8px', color: '#94A3B8' }} onClick={() => handleDelete(s.id)}>🗑️</button>
                    </div>
                  </div>
                  <h3 style={{ fontFamily: 'var(--font-ui)', fontSize: '1.15rem', lineHeight: 1.25, margin: '0 0 14px 0', color: '#1E293B', fontWeight: 900, minHeight: 58 }}>{s.ad}</h3>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 18 }}>
                    <span style={{ padding: '8px 12px', borderRadius: 12, background: '#F8FAFC', color: '#475569', border: '1px solid #E2E8F0', fontWeight: 800, fontSize: '0.78rem' }}>ŞUBE: {s.sube}</span>
                    <span style={{ padding: '8px 12px', borderRadius: 12, background: turRenk.bg, color: turRenk.text, border: `1px solid ${turRenk.border}`, fontWeight: 800, fontSize: '0.78rem' }}>{s.tur?.toUpperCase()}</span>
                    <span style={{ padding: '8px 12px', borderRadius: 12, background: '#F0FDF4', color: '#16A34A', border: '1px solid #BBF7D0', fontWeight: 800, fontSize: '0.78rem' }}>{s.aktif ? 'AKTİF' : 'PASİF'}</span>
                  </div>
                  <div style={{ marginTop: 'auto', borderTop: '1px solid #F1F5F9', paddingTop: 18 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                      <span style={{ fontSize: '0.95rem', fontWeight: 700, color: '#64748B' }}>👥 <strong style={{ color: '#475569' }}>{s.ogrenci_sayisi || 0}</strong> / {s.kapasite}</span>
                      <span style={{ fontFamily: 'var(--font-ui)', fontWeight: 800, fontSize: '0.85rem', color: '#94A3B8' }}>%{Math.round(oran)}</span>
                    </div>
                    <div style={{ height: 10, borderRadius: 999, background: '#F1F5F9', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${oran}%`, borderRadius: 999, background: progressRenk }} />
                    </div>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', borderTop: '1px solid #E2E8F0', background: '#F8FAFC' }}>
                  <button className="btn btn-ghost" style={{ borderRadius: 0, justifyContent: 'center', padding: '15px 12px', fontSize: '0.9rem', fontWeight: 800, color: '#475569' }} onClick={() => openIhtiyacModal(s)}>📖 Müfredat & Program</button>
                  <button className="btn btn-ghost" style={{ borderRadius: 0, justifyContent: 'center', padding: '15px 12px', fontSize: '0.9rem', fontWeight: 800, color: 'var(--accent)', borderLeft: '1px solid #E2E8F0' }} onClick={() => navigate(`/admin/ogrenciler?sinif_id=${s.id}&new=1`)}>+ Yeni Öğrenci Ekle</button>
                </div>
              </div>
            );
          })}
      </div>

      {showIhtiyac && (
        <div className="modal-backdrop" onClick={() => setShowIhtiyac(null)}>
          <div className="modal" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">🏫 {showIhtiyac.ad}</div>
              <button className="modal-close" onClick={() => setShowIhtiyac(null)}>✕</button>
            </div>

            {/* Sekme Seçici */}
            <div style={{ display: 'flex', gap: 4, background: 'var(--bg-elevated)', padding: 4, borderRadius: 10, marginBottom: 16 }}>
              <button
                onClick={() => setIhtiyacTab('mufredat')}
                style={{
                  flex: 1, padding: '8px 0', border: 'none', borderRadius: 8, fontSize: '0.84rem',
                  fontWeight: 700, cursor: 'pointer',
                  background: ihtiyacTab === 'mufredat' ? 'var(--accent)' : 'transparent',
                  color: ihtiyacTab === 'mufredat' ? '#fff' : 'var(--text-muted)'
                }}
              >📚 Müfredat</button>
              <button
                onClick={() => setIhtiyacTab('eskiProgram')}
                style={{
                  flex: 1, padding: '8px 0', border: 'none', borderRadius: 8, fontSize: '0.84rem',
                  fontWeight: 700, cursor: 'pointer',
                  background: ihtiyacTab === 'eskiProgram' ? '#F59E0B' : 'transparent',
                  color: ihtiyacTab === 'eskiProgram' ? '#fff' : 'var(--text-muted)'
                }}
              >🗂 Eski Programlar {eskiProgramlar.length > 0 ? `(${eskiProgramlar.length})` : ''}</button>
            </div>

            {/* Müfredat Sekmesi */}
            {ihtiyacTab === 'mufredat' && (
              <>
                <div style={{ background: 'var(--bg-elevated)', padding: 12, borderRadius: 10, marginBottom: 16, fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                  Bu sınıfın haftalık hangi dersten kaç saate ihtiyacı olduğunu belirleyin.
                </div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', marginBottom: 20 }}>
                  <div className="form-group" style={{ flex: 2 }}>
                    <label className="form-label">Ders Seçin</label>
                    <select className="form-select" value={ihtiyacForm.ders_id} onChange={e => setIhtiyacForm({ ...ihtiyacForm, ders_id: e.target.value })}>
                      <option value="">Seçiniz...</option>
                      {dersler.map(d => <option key={d.id} value={d.id}>{d.ad}</option>)}
                    </select>
                  </div>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label className="form-label">Haftalık Saat</label>
                    <input className="form-input" type="number" min="1" max="15" value={ihtiyacForm.haftalik_saat} onChange={e => setIhtiyacForm({ ...ihtiyacForm, haftalik_saat: parseInt(e.target.value) || 1 })} />
                  </div>
                  <button className="btn btn-primary" style={{ height: 42, padding: '0 16px' }} onClick={handleAddIhtiyac}>Ekle</button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 300, overflowY: 'auto' }}>
                  {ihtiyaclar.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)' }}>Henüz ders eklenmedi.</div>
                  ) : ihtiyaclar.map(iht => (
                    <div key={iht.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: 'var(--bg-elevated)', borderRadius: 8, borderLeft: `3px solid ${iht.renk || 'var(--accent)'}` }}>
                      <div>
                        <div style={{ fontWeight: 700 }}>{iht.ders_adi}</div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Haftalık: <strong>{iht.haftalik_saat} Saat</strong></div>
                      </div>
                      <button className="btn btn-sm btn-ghost" style={{ color: '#EF4444' }} onClick={() => handleDeleteIhtiyac(iht.id)}>Sil</button>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Eski Programlar Sekmesi */}
            {ihtiyacTab === 'eskiProgram' && (
              <div style={{ display: 'flex', gap: 12 }}>
                {/* Sol: program listesi */}
                <div style={{ width: 180, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 380, overflowY: 'auto' }}>
                  {eskiProgramlar.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)', fontSize: '0.82rem' }}>
                      Kayıtlı program yok
                    </div>
                  ) : eskiProgramlar.map(ep => {
                    const tarih = new Date(ep.created_at).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric' });
                    return (
                      <button key={ep.id}
                        onClick={() => handleEskiProgramSec(ep.id)}
                        style={{
                          padding: '10px 12px', borderRadius: 10, border: '1.5px solid',
                          borderColor: seciliEskiId === ep.id ? '#F59E0B' : 'var(--border)',
                          background: seciliEskiId === ep.id ? '#FFF7ED' : 'var(--bg-elevated)',
                          color: seciliEskiId === ep.id ? '#92400E' : 'var(--text-secondary)',
                          textAlign: 'left', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600
                        }}>
                        <div style={{ fontWeight: 800, fontSize: '0.8rem', marginBottom: 2 }}>{ep.ad}</div>
                        <div style={{ opacity: 0.7 }}>📅 {tarih}</div>
                      </button>
                    );
                  })}
                </div>

                {/* Sağ: Bu sınıfa ait dersler */}
                <div style={{ flex: 1, maxHeight: 380, overflowY: 'auto' }}>
                  {!seciliEskiId ? (
                    <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                      Soldan bir program seçin
                    </div>
                  ) : !eskiProgramDetay ? (
                    <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Yükleniyor...</div>
                  ) : eskiProgramDetay.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                      Bu sınıf için program kaydı yok
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {['Pazartesi', 'Sali', 'Carsamba', 'Persembe', 'Cuma', 'Cumartesi', 'Pazar'].map(gun => {
                        const gunDersler = eskiProgramDetay.filter(p => p.gun === gun);
                        if (gunDersler.length === 0) return null;
                        return (
                          <div key={gun}>
                            <div style={{ fontSize: '0.72rem', fontWeight: 800, color: 'var(--accent)', fontFamily: 'var(--font-ui)', marginBottom: 4 }}>
                              {GUN_ETIKETLERI[gun]}
                            </div>
                            {gunDersler.sort((a, b) => a.baslangic_saati?.localeCompare(b.baslangic_saati)).map((p, i) => (
                              <div key={i} style={{
                                display: 'flex', gap: 8, alignItems: 'center',
                                padding: '6px 10px', background: 'var(--bg-elevated)',
                                borderRadius: 8, marginBottom: 4,
                                borderLeft: `3px solid ${p.renk || '#3B82F6'}`
                              }}>
                                <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontFamily: 'monospace', minWidth: 48 }}>
                                  {p.baslangic_saati}
                                </span>
                                <span style={{ fontWeight: 700, fontSize: '0.8rem' }}>{p.ders_adi}</span>
                                <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginLeft: 'auto' }}>{p.ogretmen_adi}</span>
                              </div>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}

            <button className="btn btn-outline" style={{ width: '100%', marginTop: 16, justifyContent: 'center' }} onClick={() => setShowIhtiyac(null)}>Kapat</button>
          </div>
        </div>
      )}

      {showModal && (
        <div className="modal-backdrop" onClick={() => setShowModal(false)}>
          <div className="modal" style={{ maxWidth: 600 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">🏫 Yeni Sınıf Aç</div>
              <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label className="form-label" style={{ marginBottom: 8 }}>Eğitim Kademesi</label>
                <div style={{ display: 'flex', gap: 8, background: 'var(--bg-elevated)', padding: 6, borderRadius: 12 }}>
                  {Object.keys(KADEMELER).map(k => (
                    <button key={k} style={{ flex: 1, padding: '8px 0', border: 'none', borderRadius: 8, fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer', background: secilenKademe === k ? 'var(--accent)' : 'transparent', color: secilenKademe === k ? '#fff' : 'var(--text-muted)' }} onClick={() => handleKademeChange(k)}>{k}</button>
                  ))}
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div className="form-group">
                  <label className="form-label">Sınıf Seviyesi</label>
                  <select className="form-select" value={form.seviye} onChange={handleSeviyeChange}>
                    {KADEMELER[secilenKademe].seviyeler.map(s => <option key={s} value={s}>{s}. Sınıf / Kur</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Program / Tür</label>
                  <select className="form-select" value={form.tur} onChange={e => setForm({ ...form, tur: e.target.value })}>
                    {getTurler(form.seviye).map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div className="form-group"><label className="form-label">Şube *</label><input className="form-input" value={form.sube} onChange={e => setForm({ ...form, sube: e.target.value.toUpperCase() })} /></div>
                <div className="form-group"><label className="form-label">Kapasite</label><input className="form-input" type="number" value={form.kapasite} onChange={e => setForm({ ...form, kapasite: e.target.value })} /></div>
              </div>
              <div className="form-group" style={{ background: '#F8FAFC', padding: 16, borderRadius: 12, border: '1px dashed #CBD5E1' }}>
                <label className="form-label" style={{ color: '#64748B' }}>Oluşacak Sınıf Adı</label>
                <input className="form-input" style={{ fontWeight: 800, color: 'var(--accent)', fontSize: '1.1rem' }} value={form.ad} onChange={e => setForm({ ...form, ad: e.target.value })} />
              </div>
              <button className="btn btn-primary" style={{ justifyContent: 'center', padding: '12px' }} onClick={handleCreate}>✓ Sınıfı Kaydet</button>
            </div>
          </div>
        </div>
      )}

      {showEdit && (
        <div className="modal-backdrop" onClick={() => setShowEdit(null)}>
          <div className="modal" style={{ maxWidth: 500 }} onClick={e => e.stopPropagation()}>
            <h3 className="modal-title">✏️ Sınıf Düzenle</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">Sınıf Seviyesi</label>
                  <select className="form-select" value={editForm.seviye || '11'} onChange={e => { const s = e.target.value; setEditForm({ ...editForm, seviye: s, tur: getTurler(s)[0] }); }}>
                    {['5', '6', '7', '8', '9', '10', '11', '12', 'Mezun', 'Genel', 'Yetişkin', 'Çocuk'].map(s => <option key={s} value={s}>{s}. Sınıf / Kur</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Program / Tür</label>
                  <select className="form-select" value={editForm.tur || ''} onChange={e => setEditForm({ ...editForm, tur: e.target.value })}>
                    {getTurler(editForm.seviye || '11').map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-group"><label className="form-label">Sınıf Adı *</label><input className="form-input" value={editForm.ad || ''} onChange={e => setEditForm({ ...editForm, ad: e.target.value })} /></div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group"><label className="form-label">Şube</label><input className="form-input" value={editForm.sube || ''} onChange={e => setEditForm({ ...editForm, sube: e.target.value.toUpperCase() })} /></div>
                <div className="form-group"><label className="form-label">Kapasite</label><input className="form-input" type="number" value={editForm.kapasite || 30} onChange={e => setEditForm({ ...editForm, kapasite: +e.target.value })} /></div>
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
                <button className="btn btn-primary" style={{ flex: 1, justifyContent: 'center' }} onClick={handleEdit}>✓ Kaydet</button>
                <button className="btn btn-outline" onClick={() => setShowEdit(null)}>İptal</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// DERSLER YÖNETİMİ (DEĞIŞMEDEN)
// ══════════════════════════════════════════════════════════════════════════════
export function DerslerPage() {
  const [dersler, setDersler] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showEdit, setShowEdit] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [form, setForm] = useState({ ad: '', kisaltma: '', renk: '#3B82F6', sinav_turu: 'TYT', max_soru: 40 });

  const load = async () => {
    const { data } = await axios.get('/dersler');
    if (data.success) setDersler(data.data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    if (!form.ad) { toast.error('Ders adı zorunlu'); return; }
    try {
      await axios.post('/dersler', form);
      toast.success('Ders oluşturuldu!');
      setShowModal(false);
      setForm({ ad: '', kisaltma: '', renk: '#3B82F6', sinav_turu: 'TYT', max_soru: 40 });
      load();
    } catch (e) { toast.error(e.response?.data?.message || 'Hata'); }
  };

  const handleEdit = async () => {
    try { await axios.put(`/dersler/${showEdit}`, editForm); toast.success('Güncellendi!'); setShowEdit(null); load(); }
    catch { toast.error('Güncelleme hatası'); }
  };

  const SINAV_TURLERI = ['TYT', 'AYT', 'LGS', 'Genel'];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }} className="animate-fadeInUp">
        <div>
          <h2 style={{ fontFamily: 'var(--font-ui)', fontWeight: 900 }}>📚 Ders Yönetimi</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>{dersler.length} ders tanımlı</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>+ Yeni Ders</button>
      </div>

      <div className="table-container animate-fadeInUp">
        <table className="table">
          <thead><tr><th>Ders</th><th>Kısaltma</th><th>Sınav Türü</th><th>Max Soru</th><th>İşlem</th></tr></thead>
          <tbody>
            {loading ? Array(5).fill(0).map((_, i) => <tr key={i}>{Array(5).fill(0).map((_, j) => <td key={j}><div className="skeleton" style={{ height: 16 }} /></td>)}</tr>) :
              dersler.length === 0 ? <tr><td colSpan={5} style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)' }}>Ders yok</td></tr> :
                dersler.map((d, i) => (
                  <tr key={i}>
                    <td><div style={{ display: 'flex', alignItems: 'center', gap: 10 }}><div style={{ width: 10, height: 10, borderRadius: '50%', background: d.renk || '#3B82F6' }} /><span style={{ fontWeight: 700 }}>{d.ad}</span></div></td>
                    <td><span className="badge badge-info">{d.kisaltma}</span></td>
                    <td><span className="badge badge-muted">{d.sinav_turu}</span></td>
                    <td style={{ fontFamily: 'var(--font-ui)', fontWeight: 700 }}>{d.max_soru}</td>
                    <td><button className="btn btn-outline btn-sm" onClick={() => { setEditForm({ ...d }); setShowEdit(d.id); }}>✏️</button></td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-backdrop" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3 className="modal-title">📚 Yeni Ders</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div className="form-group"><label className="form-label">Ders Adı *</label><input className="form-input" placeholder="Matematik" value={form.ad} onChange={e => setForm({ ...form, ad: e.target.value })} /></div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group"><label className="form-label">Kısaltma</label><input className="form-input" placeholder="MAT" value={form.kisaltma} onChange={e => setForm({ ...form, kisaltma: e.target.value })} /></div>
                <div className="form-group"><label className="form-label">Renk</label><div style={{ display: 'flex', gap: 8, alignItems: 'center' }}><input type="color" value={form.renk} onChange={e => setForm({ ...form, renk: e.target.value })} style={{ width: 40, height: 36, border: '1.5px solid var(--border)', borderRadius: 8, cursor: 'pointer', padding: 2 }} /><input className="form-input" value={form.renk} onChange={e => setForm({ ...form, renk: e.target.value })} style={{ flex: 1 }} /></div></div>
                <div className="form-group"><label className="form-label">Sınav Türü</label><select className="form-input" value={form.sinav_turu} onChange={e => setForm({ ...form, sinav_turu: e.target.value })}>{SINAV_TURLERI.map(t => <option key={t}>{t}</option>)}</select></div>
                <div className="form-group"><label className="form-label">Max Soru</label><input className="form-input" type="number" value={form.max_soru} onChange={e => setForm({ ...form, max_soru: +e.target.value })} /></div>
              </div>
              <div style={{ display: 'flex', gap: 10 }}><button className="btn btn-primary" style={{ flex: 1, justifyContent: 'center' }} onClick={handleCreate}>✓ Oluştur</button><button className="btn btn-outline" onClick={() => setShowModal(false)}>İptal</button></div>
            </div>
          </div>
        </div>
      )}

      {showEdit && (
        <div className="modal-backdrop" onClick={() => setShowEdit(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3 className="modal-title">✏️ Ders Düzenle</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div className="form-group"><label className="form-label">Ders Adı *</label><input className="form-input" value={editForm.ad || ''} onChange={e => setEditForm({ ...editForm, ad: e.target.value })} /></div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group"><label className="form-label">Kısaltma</label><input className="form-input" value={editForm.kisaltma || ''} onChange={e => setEditForm({ ...editForm, kisaltma: e.target.value })} /></div>
                <div className="form-group"><label className="form-label">Renk</label><input type="color" value={editForm.renk || '#3B82F6'} onChange={e => setEditForm({ ...editForm, renk: e.target.value })} style={{ width: '100%', height: 38, border: '1.5px solid var(--border)', borderRadius: 8, cursor: 'pointer' }} /></div>
                <div className="form-group"><label className="form-label">Sınav Türü</label><select className="form-input" value={editForm.sinav_turu || ''} onChange={e => setEditForm({ ...editForm, sinav_turu: e.target.value })}>{SINAV_TURLERI.map(t => <option key={t}>{t}</option>)}</select></div>
                <div className="form-group"><label className="form-label">Max Soru</label><input className="form-input" type="number" value={editForm.max_soru || 40} onChange={e => setEditForm({ ...editForm, max_soru: +e.target.value })} /></div>
              </div>
              <div style={{ display: 'flex', gap: 10 }}><button className="btn btn-primary" style={{ flex: 1, justifyContent: 'center' }} onClick={handleEdit}>✓ Kaydet</button><button className="btn btn-outline" onClick={() => setShowEdit(null)}>İptal</button></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
